import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  hranyRodica,
  maHolyRozmer,
  rozmerNaUlozenie,
  rozmeryVrstvy,
  zobrazenyRozmer,
} from "@/lib/rozmer";
import { nadmieraNaUlozenie, zobrazenaNadmiera } from "@/lib/nadmiera";
import {
  AlertTriangle,
  BookmarkPlus,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  FolderPlus,
  CopyPlus,
  Crop,
  EllipsisVertical,
  GripVertical,
  Layers,
  ListPlus,
  PackagePlus,
  Plus,
  RectangleHorizontal,
  StickyNote,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { SablonaSaveDialog } from "@/components/sablona/SablonaSaveDialog";
import { StromBunka } from "@/components/zakazka/StromBunka";
import {
  IconDlzkovy,
  IconKusovy,
  IconOperacia,
  IconPlosny,
  IconSklo,
} from "@/components/ui/tandem-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDeleteDielec, useUpdateDielec } from "@/api/hooks/useDielce";
import { useBulkDeleteDielce, useBulkUpdateDielce } from "@/api/hooks/useBulkDielce";
import {
  MenuRiadku,
  OddelovacMenu,
  PolozkaMenu,
  polohaZTlacidla,
  type PolohaMenu,
} from "@/components/zakazka/MenuRiadku";
import { ZamokTabulkyContext, useZamokTabulky } from "@/components/zakazka/zamokTabulky";
import { popisPoctu, useAkcieSchranky, type AkcieSchranky } from "@/lib/schrankaRiadkov";
import { useHromadneZostavy } from "@/api/hooks/useHromadneZostavy";
import {
  nastavSchrankuHran,
  schrankaZDielca,
  useSchrankaHran,
  vlozHranyNaDielce,
} from "@/lib/prenosHran";
import {
  useDeleteZostava,
  useDuplicateZostava,
  usePoradieDielcov,
  usePoradieZostav,
  useUpdateZostava,
} from "@/api/hooks/useZostavy";
import {
  poradieZostavy,
  posunOJeden,
  posunRiadok,
  rovnakePoradie,
} from "@/lib/poradieDielcov";
import { useOperacie } from "@/api/hooks/useOperacie";
import {
  OPERACIA_LEPENA_VRSTVA,
  jeLaminatRiadok,
  jeOperaciaChild,
  jeOperaciaRiadok,
  jeLepiaciPostup,
  jePostup,
  postupPopis,
} from "@/lib/operacie";
import { stitokVlastnej } from "@/lib/vlastnaPolozka";
import { useNazvyDielcov } from "@/api/hooks/useNazvyDielcov";
import {
  hrubkaMaterialu,
  hrubkyPreDielec,
  useDostupneHrubky,
  type DostupnaHrubka,
} from "@/api/hooks/useDostupneHrubky";
import { useSplitRules } from "@/api/hooks/useSplitRules";
import { useNastaveniaGlobal } from "@/api/hooks/useNastaveniaGlobal";
import { PrirezDialog } from "@/components/dielec/PrirezDialog";
import { cncStav, mmText, type CncStav } from "@/lib/cncPrirez";
import { pravidloPreHrubku } from "@/lib/splitRules";
import { LepenyConvertDialog } from "@/components/dielec/LepenyConvertDialog";
import { PridatOperaciuDialog } from "@/components/dielec/PridatOperaciuDialog";
import { LaminovatDialog, VyberLaminatuDialog } from "@/components/dielec/LaminovatDialog";
import { CelokRiadok, NovyCelokDialog, PodmenuDoCelku } from "@/components/zakazka/Celky";
import {
  usePoradieCelkov,
  usePremenujCelok,
  useVytvorCelok,
  useZaradDoCelku,
  useZrusCelok,
} from "@/api/hooks/useCelky";
import { skupinyZostav, susedVSkupine } from "@/lib/celky";
import { KovanieChangeDialog } from "@/components/dielec/KovanieChangeDialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { pismena } from "@/lib/dielecKod";
import { maChybuHrany, popisStavuHran } from "@/lib/stavHran";
import { farbaMaterialu } from "@/lib/farbaMaterialu";
import { NacrtDielca, popisHranNacrtu } from "@/components/zakazka/NacrtDielca";
import { pridaneHranyPodtext } from "@/lib/pridaneHrany";
import {
  formatCisloBezNul,
  formatEur,
  formatMnozstvoZostavy,
  materialPrimaryId,
  menoKovania,
  normalizeNum,
  parseDecimal,
  sklonuj,
  toNum,
  vMetroch,
  zobrazRozmer,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { setLastSlot } from "@/lib/lastSlot";
import { slotyPreDielec } from "@/lib/druhSlotu";
import {
  klucZbalenia,
  nacitajZbalenie,
  ulozZbalenie,
  type StavZbalenia,
} from "@/lib/zbalenieTabulky";
import { useMe } from "@/api/hooks/useAuth";
import type {
  Celok,
  Dielec,
  DielecUpdate,
  MaterialSlot,
  NazovDielca,
  Operation,
  SplitRule,
  Varianta,
  Zostava,
} from "@/api/schemas";

/**
 * Jedna veľká tabuľka so zostavami a dielcami v hierarchii — editovateľná
 * ako Excel:
 *
 * - Zostavy = parent rows (expand/collapse), inline edit nazov/ks,
 *   akcie: + dielec (quick-add bez dialógu), duplikovať (aj s dielcami),
 *   zmazať.
 * - Dielce = child rows: JEDEN klik kdekoľvek = SELECT (DielecDetailPanel),
 *   DVOJKLIK na bunku = edit (nazov/operácia/Ks/X/Y/Z). Klik na Nadmieru
 *   prepína ✓/✗, výber slotu cez inline select; akcie: duplikovať, zmazať.
 * - Výrobný postup: cez akú dielňu dielec ide — **len výber** zo zoznamu,
 *   ktorý si user píše v Nastaveniach (KOLO 108). Voľný text sa dovtedy
 *   ukladal do `nazov_volny`, čo je **záložný názov dielca** — objavil sa
 *   tak v rozkrese tabúľ aj v podklade pre pílu.
 * - Operácie: čo s dielcom spraví stroj (rez / združ 2× / rot) — to si
 *   appka počíta sama a píše tlmene — plus vlastný text usera
 *   (`operacie_popis`) plnou farbou. Dvojklik edituje len ten text.
 * - Klávesnica: Enter/F2 otvorí bunku, Enter uloží, Esc zruší,
 *   Tab uloží a skočí na ďalšiu bunku riadku.
 * - Quick-add: nový dielec dostane kód {zostavaKod}_p{n} a rovno sa
 *   otvorí editácia názvu (focusDielecId).
 */

/** Stabilná prázdna množina — nový `new Set()` pri každom rendere by rušil memo. */
const PRAZDNY_VYBER: ReadonlySet<string> = new Set<string>();

/** „3 zostavy", „5 zostáv" — hlavička menu a hlášky viacvýberu (KOLO 196). */
function popisZostav(n: number): string {
  return `${n} ${sklonuj(n, ["zostava", "zostavy", "zostáv"])}`;
}

/**
 * Akcie menu riadku, ktoré potrebujú celú tabuľku (KOLO 181): schránka,
 * viacvýber v poradí kusovníka a hromadné zmazanie / vloženie hrán.
 * Kontext namiesto ďalších props cez `ZostavaSection` do troch typov
 * riadkov.
 */
interface AkcieTabulky {
  schranka: AkcieSchranky;
  /** Viacvýber v poradí tabuľky, keď je v ňom riadok — inak len riadok. */
  ciele: (d: Dielec) => Dielec[];
  zmazRiadky: (riadky: Dielec[]) => Promise<void>;
  vlozHrany: (riadky: Dielec[]) => Promise<void>;
  /** Laminovať dosky (KOLO 234) — okno s výberom laminátu a strán. */
  laminuj?: (riadky: Dielec[]) => void;
  /** Menu riadku (KOLO 184): pridať do zostavy riadku a zbaliť všetko. */
  pridajDielec: (zostavaId: string) => void;
  pridajKovanie?: (zostavaId: string) => void;
  /** Cenovka z hrany (KOLO 197). */
  pridajCenovku?: (zostavaId: string) => void;
  /** Vlastná položka mimo cenníka (KOLO 210). */
  pridajVlastnu?: (zostavaId: string) => void;
  /** Nová zostava (KOLO 196) — pod danú, `null` = na koniec. */
  pridajZostavu?: (poZostaveId: string | null) => void;
  /** Celky varianty (KOLO 235) — podmenu Dať do celku v menu pásu. */
  celky: Celok[];
  datDoCelku: (zostavy: Zostava[], celokId: string | null) => void;
  novyCelok: (zostavy: Zostava[]) => void;
  vsetkoRozbalene: boolean;
  prepniVsetko: () => void;
  /**
   * Výber viacerých zostáv (KOLO 196) — keď je v ňom zostava, akcia ide na
   * všetky vybrané v poradí tabuľky, inak len na ňu.
   */
  cieleZostav: (z: Zostava) => Zostava[];
  kodZostavy: (id: string) => string | null;
  duplikujZostavy: (zostavy: Zostava[]) => Promise<void>;
  zmazZostavy: (zostavy: Zostava[]) => Promise<void>;
}

const AkcieTabulkyContext = createContext<AkcieTabulky | null>(null);

/**
 * Pravé tlačidlo do poľa, kde sa práve píše, ostáva menu prehliadača
 * (kopírovať / vložiť text). Všade inde na riadku je menu riadku — aj na
 * výberovom checkboxe a na rozbaľovačkách Výrobný postup a Materiál.
 * User (KOLO 184): *„niekde funguje a niekde sa otvorí pravý klik
 * prehliadača"* — dovtedy sa menu prehliadača otváralo na každom
 * `input`/`select` a Materiál je najširší stĺpec riadku.
 */
function jeVstup(t: EventTarget | null): boolean {
  return (
    t instanceof HTMLElement &&
    t.closest(
      'input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable]',
    ) != null
  );
}

/** Koľko selektovateľných dielcov zostavy je vo výbere — pre indeterminate. */
function stavVyberuZostavy(
  z: Zostava,
  vybrane: ReadonlySet<string>,
): { vsetky: boolean; niektore: boolean } {
  const ids = ((z.dielce ?? []) as Dielec[])
    .filter((d) => !d.parent_dielec_id && !jeOperaciaRiadok(d))
    .map((d) => d.id);
  const kolko = ids.filter((i) => vybrane.has(i)).length;
  return { vsetky: ids.length > 0 && kolko === ids.length, niektore: kolko > 0 };
}

/**
 * Bunka s checkboxom výberu. `onToggle === undefined` znamená „tento riadok
 * sa vyberať nedá" (operačný riadok, lepená vrstva) — bunka ostane prázdna,
 * aby sa stĺpce nerozsypali.
 */
function VyberCell({
  zaskrtnute,
  ciastocne = false,
  onToggle,
  label,
}: {
  zaskrtnute?: boolean;
  /** Zostava, ktorej je vybraná len časť dielcov — indeterminate. */
  ciastocne?: boolean;
  onToggle?: () => void;
  label?: string;
}): JSX.Element {
  if (!onToggle) return <TableCell className="px-1" />;
  return (
    <TableCell className="px-1 text-center">
      <input
        type="checkbox"
        checked={!!zaskrtnute}
        ref={(el) => {
          if (el) el.indeterminate = !zaskrtnute && ciastocne;
        }}
        onChange={onToggle}
        // Klik na checkbox nesmie zmeniť výber riadku pod ním
        onClick={(e) => e.stopPropagation()}
        aria-label={label}
        title={label}
        className="h-3.5 w-3.5 cursor-pointer accent-primary"
      />
    </TableCell>
  );
}

export interface ZakazkaFlatTableProps {
  zakazkaId: string;
  varianta: Varianta;
  selectedDielecId: string | null;
  onSelectDielec: (id: string | null) => void;
  /** Klik na riadok zostavy — detail produktu (marža, cena). */
  selectedZostavaId?: string | null;
  onSelectZostava?: (id: string | null) => void;
  /** Quick-add — vytvorí dielec s defaultmi rovno v tabuľke. */
  onAddDielec: (zostavaId: string) => void;
  /** Otvorí dialóg pridania kovania do danej zostavy. */
  onAddKovanie?: (zostavaId: string) => void;
  /** Cenovka z hrany (KOLO 197) — v menu riadku a tabuľky. */
  onAddCenovka?: (zostavaId: string) => void;
  /** Vlastná položka mimo cenníka (KOLO 210) — v menu riadku a tabuľky. */
  onAddVlastna?: (zostavaId: string) => void;
  /** ID čerstvo vytvoreného dielca — riadok sa otvorí v edit móde názvu. */
  focusDielecId?: string | null;
  onFocusHandled?: () => void;
  /**
   * Viacnásobný výber pre hromadnú úpravu. Stav žije v editore (potrebuje ho
   * panel nad tabuľkou), poradie riadkov ale pozná len tabuľka — preto sa
   * rozsah (Shift+klik) počíta tu a hore ide už hotová množina.
   */
  vybraneDielce?: Set<string>;
  onVybraneChange?: (ids: Set<string>) => void;
  /**
   * Výber viacerých zostáv Ctrl/Shift+klikom na pás (KOLO 196). Ako pri
   * dielcoch žije v editore — ten podľa neho smeruje Pridať dielec /
   * kovanie a klávesy.
   */
  vybraneZostavy?: Set<string>;
  onVybraneZostavyChange?: (ids: Set<string>) => void;
  /**
   * Nová zostava (KOLO 196) — pod danú, `null` = na koniec; `doCelku` =
   * na koniec celku (KOLO 235).
   */
  onAddZostava?: (poZostaveId: string | null, doCelku?: string | null) => void;
  /**
   * Ručne zadávané rozmery sú bez hrán (KOLO 131) — prepínač zákazky.
   * Bunky Dĺžka/Šírka potom posielajú holý rozmer a backend k nemu hrany
   * pripočíta.
   */
  rozmeryBezHran?: boolean;
  /** Číslo zákazky do hlášky pri vložení v inej zákazke (KOLO 181). */
  zakazkaCislo?: string | null;
  /**
   * Zbalenie si pamätá prehliadač — per používateľ a varianta (KOLO 193);
   * bez uloženého stavu začne všetko zbalené. Zapína ho editor zákazky;
   * bez neho sú zostavy rozbalené a nič sa neukladá.
   */
  pamatajZbalenie?: boolean;
  /**
   * Označiť v tabuľke (KOLO 233) — riadky z panela Materiály / Hrany /
   * Kovania: tabuľka rozbalí ich zostavy (a dielce, keď je zhoda vo
   * vrstve) a posunie sa na prvý. Nový `token` = nové odhalenie, aj keď
   * sú riadky tie isté. Výber riadkov nastaví editor cez `vybraneDielce`.
   */
  odhalit?: { dielce: string[]; rozbalitDielce: string[]; token: number } | null;
  /**
   * Zámok (KOLO 231) — prečo sa tabuľka nedá upravovať (obchodník
   * v nábytku mení len cenu v paneli zostavy). Bunky len zobrazia,
   * výbery, ťahanie a položky menu, ktoré menia zákazku, sú sivé s týmto
   * dôvodom. Viacvýber vypne editor (bez `onVybraneChange`).
   */
  zamok?: string | null;
}

export function ZakazkaFlatTable({
  zakazkaId,
  varianta,
  rozmeryBezHran = false,
  zakazkaCislo = null,
  selectedDielecId,
  onSelectDielec,
  selectedZostavaId = null,
  onSelectZostava,
  onAddDielec,
  onAddKovanie,
  onAddCenovka,
  onAddVlastna,
  focusDielecId = null,
  onFocusHandled,
  vybraneDielce,
  onVybraneChange,
  vybraneZostavy,
  onVybraneZostavyChange,
  onAddZostava,
  odhalit = null,
  pamatajZbalenie = false,
  zamok = null,
}: ZakazkaFlatTableProps): JSX.Element {
  const zostavy = (varianta.zostavy ?? []) as Zostava[];
  // Celky (KOLO 235) — nadpisy nad zostavami; bez celku navrchu
  const celky = (varianta.celky ?? []) as Celok[];
  const skupiny = skupinyZostav(zostavy, celky);
  const sloty = (varianta.sloty ?? []) as MaterialSlot[];
  const slotById = new Map(sloty.map((s) => [s.id, s]));
  const zostavaKody = zostavy.map((z) => ({ id: z.id, kod: z.kod }));
  // Posúvanie zostáv (KOLO 183) — ťahadlo v páse, menu pásu, Alt+↑/↓
  // v editore. Server chce celé poradie zostáv varianty.
  const poradieZostav = usePoradieZostav(zakazkaId);
  const idsZostav = zostavy.map((z) => z.id);
  const vsetkyZostavy = new Set(idsZostav);
  const [cielZostavy, setCielZostavy] = useState<{ id: string; za: boolean } | null>(
    null,
  );

  function posliPoradieZostav(
    nove: string[],
    id: string,
    celkyZmena?: Record<string, string | null>,
  ): void {
    if (rovnakePoradie(nove, idsZostav) && !celkyZmena) return;
    const kod = zostavy.find((z) => z.id === id)?.kod ?? "";
    poradieZostav.mutate(
      {
        variantaId: varianta.id,
        zostavy: nove,
        celky: celkyZmena,
        popis: `posunutie zostavy ${kod}`,
      },
      {
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Zostavu sa nepodarilo posunúť",
            description: extractErrorMessage(err),
          }),
      },
    );
  }

  function tahanieZostavy(z: Zostava): TahanieRiadku {
    return {
      mime: ZOSTAVA_MIME,
      ciel: cielZostavy?.id === z.id ? (cielZostavy.za ? "pod" : "nad") : null,
      onNad: (za) =>
        setCielZostavy((c) => (c?.id === z.id && c.za === za ? c : { id: z.id, za })),
      onOdchod: () => setCielZostavy((c) => (c?.id === z.id ? null : c)),
      onPustenie: (id, za) => {
        setCielZostavy(null);
        // Pás pustený na zostavu iného celku ide do jej celku (KOLO 235)
        const tahana = zostavy.find((x) => x.id === id);
        const celok = z.celok_id ?? null;
        posliPoradieZostav(
          posunRiadok(idsZostav, vsetkyZostavy, id, z.id, za),
          id,
          tahana && (tahana.celok_id ?? null) !== celok ? { [id]: celok } : undefined,
        );
      },
      onKoniec: () => setCielZostavy(null),
    };
  }

  function posunZostavy(z: Zostava): PosunZostavy {
    // Hore / dole len v celku (KOLO 235) — do iného sa ťahá alebo Dať do celku
    return {
      hore: susedVSkupine(zostavy, z.id, -1),
      dole: susedVSkupine(zostavy, z.id, 1),
      onPosun: (smer) =>
        posliPoradieZostav(posunOJeden(idsZostav, vsetkyZostavy, z.id, smer), z.id),
    };
  }
  // Výrobné postupy z Nastavení — položky rozbaľovačky stĺpca Výrobný
  // postup. Ťahá sa **celý** číselník a filtruje sa `jePostup`, nie
  // `systemove: true`: postup, ktorý si user založí sám, je
  // `je_systemova=False` a do zoznamu by sa nedostal, hoci ho tam práve
  // pridal. Poradie určuje backend (číslo postupu), preto sa netriedi.
  const operacieQuery = useOperacie();
  const operacie = (operacieQuery.data?.data ?? []).filter(jePostup);
  // Prednastavené názvy dielcov (Nastavenia) — datalist pre stĺpec Názov
  const nazvyQuery = useNazvyDielcov();
  const nazvyDielcov = nazvyQuery.data?.data ?? [];
  // Dostupné hrúbky (dosky + lepené) — datalist pre stĺpec Z
  const hrubky = useDostupneHrubky();
  // Split rules — zadanie lepenej hrúbky do Z otvorí konverzný dialóg
  const splitRulesQuery = useSplitRules();
  const splitRules = splitRulesQuery.data?.data ?? [];

  // Zbalenie z prehliadača (KOLO 193) — kľúč používateľ + varianta; bez
  // uloženého stavu je všetko zbalené (user: „nech začne vždy zbalená")
  const pouzivatel = useMe().data?.data.user?.username ?? "";
  const klucStavu = pamatajZbalenie ? klucZbalenia(pouzivatel, varianta.id) : null;
  function pociatocnyStav(): StavZbalenia {
    if (klucStavu === null) return { zbalene: [], rozbalene: [] };
    return nacitajZbalenie(klucStavu) ?? { zbalene: idsZostav, rozbalene: [] };
  }

  // Invertovaný stav: držíme ZBALENÉ zostavy — nové (aj quick-add-nuté)
  // sú tak automaticky rozbalené bez re-inicializácie.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(pociatocnyStav().zbalene),
  );

  // Rozbalené vrstvy/operácie dielcov. Do KOLA 101 žil tento stav
  // v `ZostavaSection`, teda **jedna inštancia na zostavu** — a globálne
  // „rozbaliť všetko" sa k nemu nemalo ako dostať. Sémantika je opačná
  // než pri `collapsed`: tu držíme ROZBALENÉ (default je zbalené).
  const [expandedVrstvy, setExpandedVrstvy] = useState<Set<string>>(
    () => new Set(pociatocnyStav().rozbalene),
  );

  // Iná varianta (alebo používateľ) v tom istom editore — tabuľka sa
  // neprekresľuje nanovo, stav sa načíta pre nový kľúč ešte v tomto rendri
  const [nacitanyKluc, setNacitanyKluc] = useState(klucStavu);
  if (nacitanyKluc !== klucStavu) {
    const stav = pociatocnyStav();
    setNacitanyKluc(klucStavu);
    setCollapsed(new Set(stav.zbalene));
    setExpandedVrstvy(new Set(stav.rozbalene));
  }

  useEffect(() => {
    if (klucStavu === null || nacitanyKluc !== klucStavu) return;
    ulozZbalenie(klucStavu, {
      zbalene: [...collapsed],
      rozbalene: [...expandedVrstvy],
    });
  }, [klucStavu, nacitanyKluc, collapsed, expandedVrstvy]);

  // Označiť v tabuľke (KOLO 233): rozbaliť zostavy s riadkami a posunúť
  // sa na prvý. Posun len tu — klik na riadok tabuľkou ďalej nehýbe
  // (KOLO 67), toto je skok z panela na miesto v tabuľke.
  const odhalitToken = odhalit?.token;
  useEffect(() => {
    if (!odhalit || odhalit.dielce.length === 0) return undefined;
    const ciele = new Set(odhalit.dielce);
    const zostavyOdhalit = (varianta.zostavy ?? []).filter((z) =>
      (z.dielce ?? []).some((d) => ciele.has(d.id)),
    );
    setCollapsed((prev) => {
      const next = new Set(prev);
      for (const z of zostavyOdhalit) {
        next.delete(z.id);
        // Zbalený celok (KOLO 235) skrýva aj svoje zostavy
        if (z.celok_id) next.delete(z.celok_id);
      }
      return next;
    });
    if (odhalit.rozbalitDielce.length > 0) {
      setExpandedVrstvy((prev) => {
        const next = new Set(prev);
        for (const id of odhalit.rozbalitDielce) next.add(id);
        return next;
      });
    }
    const prvy = odhalit.dielce[0];
    const casovac = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-testid="dielec-row-${prvy}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 60);
    return () => window.clearTimeout(casovac);
    // Spúšťa len nový token — nie každý refetch varianty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odhalitToken]);

  function expandVrstvy(parentId: string): void {
    setExpandedVrstvy((prev) => new Set(prev).add(parentId));
  }

  function toggleVrstvy(parentId: string): void {
    setExpandedVrstvy((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }

  /** Dielce, ktoré majú čo rozbaliť (lepené vrstvy alebo vnorené operácie). */
  function dielceSDetmi(): string[] {
    const ids: string[] = [];
    for (const z of varianta.zostavy ?? []) {
      const rodicia = new Set<string>();
      for (const d of z.dielce ?? []) {
        if (d.parent_dielec_id) rodicia.add(d.parent_dielec_id);
      }
      ids.push(...rodicia);
    }
    return ids;
  }

  /**
   * „Rozbaliť / zbaliť všetko" (KOLO 101, žiadosť usera).
   *
   * Týka sa **aj vrstiev a operácií** — preto musel `expandedVrstvy`
   * vyjsť zo `ZostavaSection` sem hore. Dva stavy majú opačnú sémantiku
   * (`collapsed` drží zbalené zostavy, `expandedVrstvy` rozbalené
   * dielce), takže sa nastavujú „krížom".
   */
  const vsetkoRozbalene =
    collapsed.size === 0 &&
    dielceSDetmi().every((id) => expandedVrstvy.has(id));

  function prepniVsetko(): void {
    if (vsetkoRozbalene) {
      setCollapsed(new Set((varianta.zostavy ?? []).map((z) => z.id)));
      setExpandedVrstvy(new Set());
    } else {
      setCollapsed(new Set());
      setExpandedVrstvy(new Set(dielceSDetmi()));
    }
  }

  function toggleExpand(id: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // ─── Viacnásobný výber ──────────────────────────────────────
  // Kotva pre Shift+klik — posledný riadok, ktorý user vybral „naostro".
  const [kotva, setKotva] = useState<string | null>(null);
  const vybrane = vybraneDielce ?? PRAZDNY_VYBER;
  const vybraneZ = vybraneZostavy ?? PRAZDNY_VYBER;

  /**
   * Selektovateľné dielce v poradí, v akom ich vidí user. Rozsah pri
   * Shift+kliku sa počíta z TOHTO poradia, nie z poradia v dátach —
   * zbalená zostava svoje dielce do rozsahu nepridá.
   *
   * Operačné riadky a lepené vrstvy sú vynechané: paušál „Ostatné" nemá
   * slot ani hrany a vrstva má vlastnú hrúbkovú validáciu, takže hromadná
   * zmena by pre ne nemala čo nastaviť.
   */
  const selektovatelne: string[] = [];
  for (const z of zostavy) {
    if (collapsed.has(z.id)) continue;
    if (z.celok_id && collapsed.has(z.celok_id)) continue;
    for (const d of (z.dielce ?? []) as Dielec[]) {
      if (d.parent_dielec_id) continue;
      if (jeOperaciaRiadok(d)) continue;
      selektovatelne.push(d.id);
    }
  }

  /** Klik na riadok dielca — modifikátory rozhodujú o type výberu. */
  function klikDielec(id: string, e: React.MouseEvent): void {
    // Jeden druh viacvýberu naraz — dielce alebo zostavy (KOLO 196)
    if (vybraneZ.size > 0) onVybraneZostavyChange?.(new Set());
    if (!onVybraneChange) {
      onSelectDielec(id);
      return;
    }
    if (e.shiftKey && kotva) {
      const od = selektovatelne.indexOf(kotva);
      const po = selektovatelne.indexOf(id);
      if (od >= 0 && po >= 0) {
        const [a, b] = od <= po ? [od, po] : [po, od];
        onVybraneChange(new Set(selektovatelne.slice(a, b + 1)));
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      prepniVyber(id);
      setKotva(id);
      return;
    }
    // Obyčajný klik = jediný výber ako doteraz
    onVybraneChange(new Set());
    setKotva(id);
    onSelectDielec(id);
  }

  function prepniVyber(id: string): void {
    if (!onVybraneChange) return;
    const next = new Set(vybrane);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      // Prvé označenie berie aj doteraz vybraný dielec — inak by sa
      // „označím ešte tento" chápalo ako „chcem len tento".
      if (next.size === 1 && selectedDielecId && selectedDielecId !== id) {
        next.add(selectedDielecId);
      }
    }
    onVybraneChange(next);
    setKotva(id);
  }

  /** Checkbox v riadku zostavy — označí/odznačí všetky jej dielce. */
  function prepniZostavu(z: Zostava): void {
    if (!onVybraneChange) return;
    const ids = ((z.dielce ?? []) as Dielec[])
      .filter((d) => !d.parent_dielec_id && !jeOperaciaRiadok(d))
      .map((d) => d.id);
    const vsetkyVybrane = ids.length > 0 && ids.every((i) => vybrane.has(i));
    const next = new Set(vybrane);
    for (const i of ids) {
      if (vsetkyVybrane) next.delete(i);
      else next.add(i);
    }
    onVybraneChange(next);
  }

  // ─── Výber viacerých zostáv (KOLO 196) ──────────────────────
  const [kotvaZostavy, setKotvaZostavy] = useState<string | null>(null);

  /**
   * Nový výber zostáv. Jedna zostava nie je viacvýber — stane sa vybranou
   * zostavou, inak by lišta a klávesy mierili na inú, než svieti.
   */
  function nastavVyberZostav(next: Set<string>): void {
    onVybraneChange?.(new Set());
    if (next.size > 1) {
      onVybraneZostavyChange?.(next);
      return;
    }
    onVybraneZostavyChange?.(new Set());
    onSelectZostava?.(next.size === 1 ? ([...next][0] ?? null) : null);
  }

  /** Klik na pás — Ctrl prepína, Shift rozsah, obyčajný vyberie jednu. */
  function klikZostava(id: string, e: React.MouseEvent): void {
    if (!onVybraneZostavyChange) {
      onSelectZostava?.(id);
      return;
    }
    const kotvaRozsahu = kotvaZostavy ?? selectedZostavaId;
    if (e.shiftKey && kotvaRozsahu) {
      const od = idsZostav.indexOf(kotvaRozsahu);
      const po = idsZostav.indexOf(id);
      if (od >= 0 && po >= 0) {
        const [a, b] = od <= po ? [od, po] : [po, od];
        nastavVyberZostav(new Set(idsZostav.slice(a, b + 1)));
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      // Prvé označenie berie aj doteraz vybranú zostavu (ako pri dielcoch)
      const next = new Set(
        vybraneZ.size > 0 ? vybraneZ : selectedZostavaId ? [selectedZostavaId] : [],
      );
      if (next.has(id)) next.delete(id);
      else next.add(id);
      nastavVyberZostav(next);
      setKotvaZostavy(id);
      return;
    }
    onVybraneZostavyChange(new Set());
    setKotvaZostavy(id);
    onSelectZostava?.(id);
  }

  const hromadne = useHromadneZostavy(zakazkaId);
  // Menu tabuľky na pravé tlačidlo mimo riadkov (KOLO 196)
  const [menuTabulky, setMenuTabulky] = useState<PolohaMenu | null>(null);

  // ─── Menu riadku (KOLO 181) ─────────────────────────────────
  const schranka = useAkcieSchranky({ zakazkaId, zakazkaCislo, variantaId: varianta.id });
  const bulkDelete = useBulkDeleteDielce(zakazkaId);
  const bulkUpdate = useBulkUpdateDielce(zakazkaId);
  const schrankaHran = useSchrankaHran();
  // Kusovník všetkých zostáv v poradí tabuľky — aj zbalených, viacvýber
  // ich môže obsahovať (checkbox zostavy)
  const kusovnik = zostavy.flatMap((z) =>
    ((z.dielce ?? []) as Dielec[]).filter(
      (d) => !d.parent_dielec_id && !jeOperaciaRiadok(d),
    ),
  );
  // Okno Laminovať (KOLO 234) — jedno pre celú tabuľku, aj pri viacvýbere
  const [laminovat, setLaminovat] = useState<Dielec[] | null>(null);
  // Celky (KOLO 235) — Nový celok z menu pásu alebo tabuľky
  const [novyCelokZ, setNovyCelokZ] = useState<Zostava[] | null>(null);
  const vytvorCelok = useVytvorCelok(zakazkaId, varianta.id);
  const premenujCelok = usePremenujCelok(zakazkaId);
  const zrusCelok = useZrusCelok(zakazkaId, varianta.id);
  const poradieCelkov = usePoradieCelkov(zakazkaId, varianta.id);
  const zaradDoCelku = useZaradDoCelku(zakazkaId, varianta.id);

  function chybaCelku(title: string): (err: Error) => void {
    return (err) =>
      toast({ variant: "destructive", title, description: extractErrorMessage(err) });
  }

  function posunCelok(celokId: string, smer: -1 | 1): (() => void) | null {
    const ids = celky.map((c) => c.id);
    const i = ids.indexOf(celokId);
    const j = i + smer;
    if (i < 0 || j < 0 || j >= ids.length) return null;
    return () => {
      const nove = [...ids];
      [nove[i], nove[j]] = [nove[j] as string, nove[i] as string];
      const nazov = celky[i]?.nazov ?? "";
      poradieCelkov.mutate(
        { celky: nove, popis: `posunutie celku ${nazov}` },
        { onError: chybaCelku("Celok sa nepodarilo posunúť") },
      );
    };
  }

  function riadokCelku(celok: Celok, zostavyCelku: Zostava[]): JSX.Element {
    return (
      <CelokRiadok
        key={`celok-${celok.id}`}
        celok={celok}
        zostavy={zostavyCelku}
        zbaleny={collapsed.has(celok.id)}
        onToggle={() => toggleExpand(celok.id)}
        onPremenuj={(nazov) =>
          premenujCelok.mutate(
            { celokId: celok.id, nazov },
            { onError: chybaCelku("Celok sa nepodarilo premenovať") },
          )
        }
        onPridajZostavu={onAddZostava ? () => onAddZostava(null, celok.id) : undefined}
        onPosun={{ hore: posunCelok(celok.id, -1), dole: posunCelok(celok.id, 1) }}
        onZrus={() =>
          zrusCelok.mutate(
            { celokId: celok.id, nazov: celok.nazov },
            {
              onSuccess: () =>
                toast({
                  title: `Celok ${celok.nazov} zrušený`,
                  description:
                    zostavyCelku.length > 0
                      ? `${popisZostav(zostavyCelku.length)} ostáva bez celku navrchu. Ctrl+Z ho vráti.`
                      : "Ctrl+Z ho vráti.",
                }),
              onError: chybaCelku("Celok sa nepodarilo zrušiť"),
            },
          )
        }
        tahanie={{
          mime: ZOSTAVA_MIME,
          onPustenie: (id) => {
            const z = zostavy.find((x) => x.id === id);
            if (z && (z.celok_id ?? null) !== celok.id) {
              akcieTabulky.datDoCelku([z], celok.id);
            }
          },
        }}
      />
    );
  }
  const akcieTabulky: AkcieTabulky = {
    laminuj: (riadky) => {
      const dosky = riadky.filter((d) => !d.parent_dielec_id);
      if (dosky.length > 0) setLaminovat(dosky);
    },
    schranka,
    ciele: (d) =>
      vybrane.has(d.id) && vybrane.size > 1
        ? kusovnik.filter((x) => vybrane.has(x.id))
        : [d],
    zmazRiadky: async (riadky) => {
      if (!window.confirm(`Zmazať ${popisPoctu(riadky.length)}?`)) return;
      const v = await bulkDelete.mutateAsync(riadky.map((d) => d.id));
      onVybraneChange?.(new Set());
      toast(
        v.chyby.length === 0
          ? { title: `Zmazané: ${popisPoctu(v.hotovo)}` }
          : {
              variant: "destructive",
              title: `Zmazané ${v.hotovo}, ${v.chyby.length} neprešlo`,
              description: v.chyby.map((c) => c.sprava).join(" · "),
            },
      );
    },
    vlozHrany: async (riadky) => {
      if (!schrankaHran) return;
      const r = await vlozHranyNaDielce(riadky, schrankaHran, {
        bezStran: (d) => Boolean(d.masiv || d.riadok_z_hrany),
        uloz: async (d, data) => {
          await bulkUpdate.mutateAsync({ ids: [d.id], data: data as DielecUpdate });
        },
      });
      const zdroj = schrankaHran.kod ?? "dielca";
      if (r.chyby.length > 0) {
        toast({
          variant: "destructive",
          title: `Hrany z ${zdroj} — ${r.chyby.length} neprešlo`,
          description: r.chyby.map((c) => c.sprava).join(" · "),
        });
      } else if (r.ulozene.length > 0) {
        toast({ title: `Hrany z ${zdroj} — ${popisPoctu(r.ulozene.length)}` });
      }
      if (r.preskocene.length > 0) {
        toast({
          title: `Bez hrán (${r.preskocene.length})`,
          description: `Masív a cenovka hrany nedostanú: ${r.preskocene
            .map((d) => d.kod ?? d.id)
            .join(", ")}.`,
        });
      }
    },
    pridajDielec: onAddDielec,
    pridajKovanie: onAddKovanie,
    pridajCenovku: onAddCenovka,
    pridajVlastnu: onAddVlastna,
    pridajZostavu: onAddZostava,
    celky,
    datDoCelku: (ciele, celokId) => {
      const nazov = celky.find((c) => c.id === celokId)?.nazov;
      zaradDoCelku.mutate(
        {
          zostavaIds: ciele.map((z) => z.id),
          celokId,
          popis: nazov ? `presun do celku ${nazov}` : "presun mimo celku",
        },
        {
          onSuccess: () =>
            toast({
              title: nazov
                ? `${popisZostav(ciele.length)} v celku ${nazov}`
                : `${popisZostav(ciele.length)} bez celku`,
            }),
          onError: (err) =>
            toast({
              variant: "destructive",
              title: "Zostavu sa nepodarilo presunúť",
              description: extractErrorMessage(err),
            }),
        },
      );
    },
    novyCelok: (ciele) => setNovyCelokZ(ciele),
    vsetkoRozbalene,
    prepniVsetko,
    cieleZostav: (z) =>
      vybraneZ.has(z.id) && vybraneZ.size > 1
        ? zostavy.filter((x) => vybraneZ.has(x.id))
        : [z],
    kodZostavy: (id) => zostavy.find((z) => z.id === id)?.kod ?? null,
    duplikujZostavy: async (ciele) => {
      const v = await hromadne.duplikuj(ciele, zostavy);
      onVybraneZostavyChange?.(new Set());
      toast(
        v.chyby.length === 0
          ? { title: `Zduplikované: ${popisZostav(v.hotovo.length)}` }
          : {
              variant: "destructive",
              title: `Zduplikované ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
              description: v.chyby.map((c) => `${c.kod}: ${c.sprava}`).join(" · "),
            },
      );
    },
    zmazZostavy: async (ciele) => {
      const kody = ciele.map((z) => z.kod).join(", ");
      if (!window.confirm(`Naozaj zmazať ${popisZostav(ciele.length)} (${kody})?`)) return;
      const v = await hromadne.zmaz(ciele);
      onVybraneZostavyChange?.(new Set());
      onSelectZostava?.(null);
      toast(
        v.chyby.length === 0
          ? { title: `Zmazané: ${popisZostav(v.hotovo.length)}` }
          : {
              variant: "destructive",
              title: `Zmazané ${v.hotovo.length}, ${v.chyby.length} neprešlo`,
              description: v.chyby.map((c) => `${c.kod}: ${c.sprava}`).join(" · "),
            },
      );
    },
  };

  return (
    <AkcieTabulkyContext.Provider value={akcieTabulky}>
    <ZamokTabulkyContext.Provider value={zamok}>
    {/* Jediná scrollovacia oblasť editora — telo tabuľky. Karta dostane výšku
        od rodiča (`flex-1` v editore) a `<Table>` v nej scrolluje so sticky
        hlavičkou stĺpcov. */}
    <div
      className="flex h-full flex-col overflow-hidden rounded-lg border bg-card"
      // Pravé tlačidlo mimo riadkov — hlavička, miesto pod poslednou
      // zostavou (KOLO 196). User: „keď ťuknem dakde mimo tabuľky pravým,
      // otvorí prehliadač okno, čo nechcem". Riadky si menu otvárajú samy
      // (`preventDefault`); okná a menu v portáli do karty nepatria.
      onContextMenu={(e) => {
        if (e.defaultPrevented || jeVstup(e.target)) return;
        if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return;
        e.preventDefault();
        setMenuTabulky({ x: e.clientX, y: e.clientY });
      }}
    >
      {menuTabulky ? (
        <MenuTabulky
          poloha={menuTabulky}
          onClose={() => setMenuTabulky(null)}
          ciel={
            (vybraneZ.size > 0 ? zostavy.find((z) => vybraneZ.has(z.id)) : undefined) ??
            zostavy.find((z) => z.id === selectedZostavaId) ??
            zostavy.find((z) => (z.dielce ?? []).some((d) => d.id === selectedDielecId)) ??
            zostavy[zostavy.length - 1] ??
            null
          }
          pocetZostav={vybraneZ.size > 1 ? vybraneZ.size : 1}
        />
      ) : null}
      {/* Farebnosť a raster tabuľky dielcov (KOLO 102) sú zámerne
          scopnuté SEM, nie do `ui/table.tsx` — ten je zdieľaný Cenníkom,
          Kovaniami aj Objednávkami a globálna zmena by prekreslila
          stránky, ktoré s tým nemajú nič spoločné.
          `py-1.5` na všetkých bunkách: `TableCell` má `p-3` (~40 px), ale
          osem buniek tu je prepísaných na `p-1`/`px-1`, takže výška riadku
          padala na najvyššiu bunku a medzi riadkami sa líšila — oko nemalo
          pravidelný raster. Vodorovný padding zostáva per stĺpec. */}
      <Table
        wrapperClassName="h-full"
        className={cn(
          "[&_tbody_tr]:border-b-tab-ciara [&_td]:py-1.5 [&_th]:py-1.5",
          // Zvislé oddeľovače stĺpcov (KOLO 137) odišli v KOLE 180 —
          // tabuľka sa číta po riadkoch, čiary medzi stĺpcami len rušili
          // (návrh D, ktorý si user vybral)
        )}
      >
        <TableHeader className="bg-tab-hlavicka [&_th]:text-tab-hlavicka-text">
          <TableRow>
            {/* Výber pre hromadnú úpravu — stĺpec je viditeľný vždy, aby
                bolo z čoho uhádnuť, že sa dá označovať viac riadkov. */}
            <TableHead className="w-[30px] px-1 text-center">
              {vybrane.size > 0 ? (
                <button
                  type="button"
                  onClick={() => onVybraneChange?.(new Set())}
                  aria-label="Zrušiť výber"
                  title="Zrušiť výber (Escape)"
                  className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </TableHead>
            {/* Ikona typu riadku (dielec, sendvič, sklo, kovanie, operácia).
                Chevrony sa v KOLE 136 presunuli do stromu v stĺpci ID. */}
            {/* KOLO 180: plošný dielec tu má náčrt s hranami (34 px) */}
            <TableHead className="w-[42px] p-1" />
            {/* Jedno číslovanie (KOLO 134): zostava `A`, dielec `A1`, vrstva
                `A2a` — ten istý kód ide do exportov. Od KOLA 136 strom
                (`StromBunka`): „rozbaliť všetko" je nad chevronmi stromu,
                tam, kde ho človek hľadá (KOLO 101). Stav sa neukladá. */}
            <TableHead className="w-[100px] pl-0.5">
              <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={prepniVsetko}
                aria-label={
                  vsetkoRozbalene ? "Zbaliť všetko" : "Rozbaliť všetko"
                }
                aria-pressed={vsetkoRozbalene}
                title={
                  vsetkoRozbalene
                    ? "Zbaliť všetky zostavy aj vrstvy"
                    : "Rozbaliť všetky zostavy aj vrstvy"
                }
                className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
              >
                {vsetkoRozbalene ? (
                  <ChevronsDownUp className="h-4 w-4" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4" />
                )}
              </button>
              ID
              </div>
            </TableHead>
            <TableHead>Názov</TableHead>
            {/* Čo to je (KOLO 136) — názov drží TAN / predvoľbu (`bok_L`),
                popis slová. Poznámka pre dielňu je iné pole. */}
            <TableHead className="w-[180px]" title="Popis zostavy — čo to je (TAN je v názve)">
              Popis
            </TableHead>
            <TableHead className="w-[44px] text-right">Ks</TableHead>
            {/* Rozmery v poradí podľa smeru vlákien (Y-dielec prehodený) —
                dĺžka beží po vlákne tabule, ako TopSolid CISTA_DELKA/SIRKA */}
            <TableHead
              className="w-[70px] text-right"
              title="Rozmer v smere vlákna tabule (X; pri dielci so smerom Y je to jeho Y rozmer)"
            >
              Dĺžka
            </TableHead>
            <TableHead
              className="w-[70px] text-right"
              title="Rozmer naprieč vláknom tabule"
            >
              Šírka
            </TableHead>
            <TableHead className="w-[60px] text-right">Z mm</TableHead>
            {/* Cez akú dielňu dielec ide — vyberá sa, nepíše (KOLO 108). */}
            <TableHead
              className="w-[110px]"
              title="Cez akú dielňu dielec ide. Zoznam si píšeš v Nastaveniach → Výroba a CNC → Výrobné postupy."
            >
              Výrobný postup
            </TableHead>
            {/* Čo s dielcom spraví stroj — to si appka počíta sama; user
                k tomu dopíše svoje (lakovať, frézovať). */}
            {/* 190 px (KOLO 195) — miesto po stĺpci Materiál, ktorý ukazuje
                už len kód: „rez 302 × 51,5" + vlastný text sa zmestí */}
            <TableHead
              className="w-[190px]"
              title="Čo sa s dielcom robí na stroji (rez na rozmer, združ, rot) + tvoj vlastný text"
            >
              Operácie
            </TableHead>
            {/* Dve čísla so znamienkom (`+5 / +5`, `-0,5 / 0`) a k tomu
                ikona CNC varovania — fajka sa zmestila do 70 px, toto nie. */}
            <TableHead className="w-[84px] text-center">Nadmiera</TableHead>
            <TableHead
              className="w-[112px] text-right"
              title="Konečný (hrubý) rozmer po všetkých nadmierach — prírez na pílu"
            >
              Prírez
            </TableHead>
            {/* Len kód dosky (KOLO 195), názov v tooltipe — miesto dostali
                Operácie. Do KOLA 194 kód + názov na 254 px (KOLO 181). */}
            <TableHead className="w-[160px]">Materiál</TableHead>
            {/* ⋮ riadku (KOLO 181). Päť ikon pásu zostavy sa rozlieva cez
                Operácie…⋮ (`colSpan={5}`), takže stĺpec nenafúknu. */}
            <TableHead className="w-[34px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {zostavy.length === 0 && celky.length === 0 ? (
            <TableRow>
              <TableCell colSpan={15} className="py-8 text-center text-muted-foreground">
                Žiadne zostavy.{" "}
                {zamok ? null : onAddZostava ? (
                  <Button size="sm" variant="outline" onClick={() => onAddZostava(null)}>
                    <Plus className="h-3.5 w-3.5" />
                    Zostava
                  </Button>
                ) : (
                  <>Klikni „+ Zostava" hore.</>
                )}
              </TableCell>
            </TableRow>
          ) : (
            skupiny.flatMap((s) => [
              ...(s.celok ? [riadokCelku(s.celok, s.zostavy)] : []),
              ...(s.celok && collapsed.has(s.celok.id)
                ? []
                : s.zostavy.map((z) => (
                  <ZostavaSection
                    key={z.id}
                    zakazkaId={zakazkaId}
                    rozmeryBezHran={rozmeryBezHran}
                    zostava={z}
                    existingZostavy={zostavaKody}
                    sloty={sloty}
                    slotById={slotById}
                    operacie={operacie}
                    nazvyDielcov={nazvyDielcov}
                    hrubky={hrubky}
                    splitRules={splitRules}
                    expanded={!collapsed.has(z.id)}
                    onToggle={() => toggleExpand(z.id)}
                    expandedVrstvy={expandedVrstvy}
                    onToggleVrstvyDielca={toggleVrstvy}
                    onExpandVrstvyDielca={expandVrstvy}
                    selectedDielecId={selectedDielecId}
                    onSelectDielec={onSelectDielec}
                    zostavaSelected={
                      vybraneZ.size > 0 ? vybraneZ.has(z.id) : selectedZostavaId === z.id
                    }
                    onKlikZostava={
                      onSelectZostava || onVybraneZostavyChange
                        ? (e: React.MouseEvent) => klikZostava(z.id, e)
                        : undefined
                    }
                    onAddDielec={() => onAddDielec(z.id)}
                    onAddKovanie={onAddKovanie ? () => onAddKovanie(z.id) : undefined}
                    focusDielecId={focusDielecId}
                    onFocusHandled={onFocusHandled}
                    vybrane={vybrane}
                    onKlikDielec={klikDielec}
                    onPrepniVyber={onVybraneChange ? prepniVyber : undefined}
                    zostavaVybrana={stavVyberuZostavy(z, vybrane).vsetky}
                    zostavaCiastocne={stavVyberuZostavy(z, vybrane).niektore}
                    onToggleZostavaVyber={
                      onVybraneChange ? () => prepniZostavu(z) : undefined
                    }
                    tahanieZostavy={
                      (zostavy.length > 1 || celky.length > 0) && !zamok
                        ? tahanieZostavy(z)
                        : undefined
                    }
                    posunZostavy={posunZostavy(z)}
                  />
                  ))),
            ])
          )}
        </TableBody>
      </Table>
    </div>
    {laminovat ? (
      <LaminovatDialog
        zakazkaId={zakazkaId}
        variantaId={varianta.id}
        dielce={laminovat}
        doskaMaterialId={
          laminovat[0]?.material_id_direct ??
          (laminovat[0]?.material_slot_id
            ? (slotById.get(laminovat[0].material_slot_id)?.material_id ?? null)
            : null)
        }
        onClose={() => setLaminovat(null)}
      />
    ) : null}
    {novyCelokZ ? (
      <NovyCelokDialog
        zostavy={novyCelokZ}
        ukladam={vytvorCelok.isPending}
        onClose={() => setNovyCelokZ(null)}
        onVytvor={(nazov, zostavaIds) =>
          vytvorCelok.mutate(
            { nazov, zostava_ids: zostavaIds },
            {
              onSuccess: () => {
                setNovyCelokZ(null);
                toast({ title: `Celok ${nazov} pridaný` });
              },
              onError: chybaCelku("Celok sa nepodarilo pridať"),
            },
          )
        }
      />
    ) : null}
    </ZamokTabulkyContext.Provider>
    </AkcieTabulkyContext.Provider>
  );
}

/** Typ dát pri ťahaní riadku; za ním id zostavy — pustiť sa dá len v nej. */
const DIELEC_MIME = "application/x-tandem-dielec";
/** Ťahanie pásu zostavy (KOLO 183) — pustiť sa dá len na iný pás. */
const ZOSTAVA_MIME = "application/x-tandem-zostava";

/** Prečo je posun zostavy v rade ikon menu pásu sivý (KOLO 184). */
function dovodPosunuZostavy(
  posun: PosunZostavy | undefined,
  smer: "hore" | "dole",
): string | null {
  if (!posun || (!posun.hore && !posun.dole)) return "jediná zostava";
  if (posun[smer]) return null;
  return smer === "hore" ? "zostava je prvá" : "zostava je posledná";
}

/** Posunúť zostavu hore / dole z menu pásu (KOLO 183). */
interface PosunZostavy {
  hore: boolean;
  dole: boolean;
  onPosun: (smer: -1 | 1) => void;
}

/** Ťahanie jedného riadku — stav čiary a udalosti od `ZostavaSection`. */
interface TahanieRiadku {
  mime: string;
  /** Kam riadok pristane, keď ho pustíš nad týmto. */
  ciel: "nad" | "pod" | null;
  onNad: (za: boolean) => void;
  onOdchod: () => void;
  onPustenie: (id: string, za: boolean) => void;
  onKoniec: () => void;
}

function ZostavaSection({
  zakazkaId,
  rozmeryBezHran = false,
  zostava,
  existingZostavy,
  sloty,
  slotById,
  operacie,
  nazvyDielcov,
  hrubky,
  splitRules,
  expanded,
  onToggle,
  expandedVrstvy,
  onToggleVrstvyDielca,
  onExpandVrstvyDielca,
  selectedDielecId,
  onSelectDielec,
  zostavaSelected = false,
  onKlikZostava,
  onAddDielec,
  onAddKovanie,
  focusDielecId,
  onFocusHandled,
  vybrane,
  onKlikDielec,
  onPrepniVyber,
  zostavaVybrana,
  zostavaCiastocne,
  onToggleZostavaVyber,
  tahanieZostavy,
  posunZostavy,
}: {
  zakazkaId: string;
  rozmeryBezHran?: boolean;
  zostava: Zostava;
  existingZostavy: { id: string; kod: string }[];
  sloty: MaterialSlot[];
  slotById: Map<string, MaterialSlot>;
  operacie: Operation[];
  nazvyDielcov: NazovDielca[];
  hrubky: DostupnaHrubka[];
  splitRules: SplitRule[];
  expanded: boolean;
  onToggle: () => void;
  /** Rozbalené vrstvy/operácie — stav je od KOLA 101 v tabuľke, aby sa
      k nemu dostalo aj „rozbaliť všetko" (predtým bol per zostavu). */
  expandedVrstvy: Set<string>;
  onToggleVrstvyDielca: (dielecId: string) => void;
  onExpandVrstvyDielca: (dielecId: string) => void;
  selectedDielecId: string | null;
  onSelectDielec: (id: string | null) => void;
  zostavaSelected?: boolean;
  /** Klik na pás — výber zostavy, Ctrl/Shift viacvýber (KOLO 196). */
  onKlikZostava?: (e: React.MouseEvent) => void;
  onAddDielec: () => void;
  onAddKovanie?: () => void;
  focusDielecId: string | null;
  onFocusHandled?: () => void;
  /** Viacnásobný výber — riadky si z neho čítajú stav checkboxu. */
  vybrane: ReadonlySet<string>;
  onKlikDielec: (id: string, e: React.MouseEvent) => void;
  /** `undefined` = výber nie je napojený → riadky nemajú checkboxy. */
  onPrepniVyber?: (id: string) => void;
  zostavaVybrana: boolean;
  zostavaCiastocne: boolean;
  onToggleZostavaVyber?: () => void;
  /** Ťahanie pásu (KOLO 183); `undefined` = jediná zostava, niet kam. */
  tahanieZostavy?: TahanieRiadku;
  posunZostavy?: PosunZostavy;
}): JSX.Element {
  const dielce = (zostava.dielce ?? []) as Dielec[];
  // Lepené vrstvy (_lep1/_lep2) sú zabalené pod rodičom — v tabuľke
  // vidno len top-level dielce, vrstvy sa rozbaľujú chevronom.
  const topLevel = dielce.filter((d) => !d.parent_dielec_id);
  // Operácie zostavy („Ostatné" — paušál za spojovací materiál) idú pod
  // kusovník: v kalkulácii aj v dielni sa čítajú ako posledná položka.
  // Rieši sa to zobrazením, nie prepisom `poradie` v DB — existujúce
  // zostavy majú poradie=0 a migrovať produkčné dáta kvôli kozmetike
  // by bolo neúmerné.
  const dielceRows = topLevel.filter((d) => !jeOperaciaRiadok(d));
  const viacKusov = (zostava.pocet_ks ?? 1) > 1;
  // Zostava v metroch (KOLO 227): v páse metre a cena za meter
  const metreZostavy = vMetroch(zostava);
  const jednotkovaCena = metreZostavy || viacKusov;
  const operaciaRows = topLevel.filter(jeOperaciaRiadok);
  const vrstvyByParent = new Map<string, Dielec[]>();
  for (const d of dielce) {
    if (d.parent_dielec_id) {
      const arr = vrstvyByParent.get(d.parent_dielec_id) ?? [];
      arr.push(d);
      vrstvyByParent.set(d.parent_dielec_id, arr);
    }
  }
  // Posúvanie riadkov (KOLO 176) — len kusovník; paušál a deti dielca
  // idú so svojím miestom. Server chce celé poradie koreňových riadkov
  // bez paušálu, v tom poradí, ako ich vrátil.
  const poradieMutation = usePoradieDielcov(zakazkaId);
  const { korene: korenoveBezPausalu, posuvatelne } = poradieZostavy(dielce);
  const mimeTahania = `${DIELEC_MIME}-${zostava.id}`;
  const [cielTahania, setCielTahania] = useState<{ id: string; za: boolean } | null>(
    null,
  );

  function presun(id: string, cielId: string, za: boolean): void {
    presunNa(posunRiadok(korenoveBezPausalu, posuvatelne, id, cielId, za), id);
  }

  function presunNa(nove: string[], id: string): void {
    if (rovnakePoradie(nove, korenoveBezPausalu)) return;
    const kod = dielceRows.find((d) => d.id === id)?.kod ?? "";
    poradieMutation.mutate(
      { zostavaId: zostava.id, dielce: nove, popis: `posunutie riadku ${kod}` },
      {
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Riadok sa nepodarilo posunúť",
            description: extractErrorMessage(err),
          }),
      },
    );
  }

  function tahanieRiadku(d: Dielec): TahanieRiadku {
    return {
      mime: mimeTahania,
      ciel: cielTahania?.id === d.id ? (cielTahania.za ? "pod" : "nad") : null,
      onNad: (za) =>
        setCielTahania((c) => (c?.id === d.id && c.za === za ? c : { id: d.id, za })),
      onOdchod: () => setCielTahania((c) => (c?.id === d.id ? null : c)),
      onPustenie: (id, za) => {
        setCielTahania(null);
        presun(id, d.id, za);
      },
      onKoniec: () => setCielTahania(null),
    };
  }

  const deleteZostavaMutation = useDeleteZostava(zakazkaId);
  const duplicateZostavaMutation = useDuplicateZostava(zakazkaId);
  const updateZostavaMutation = useUpdateZostava(zakazkaId);
  const [sablonaDialogOpen, setSablonaDialogOpen] = useState(false);
  const akcie = useContext(AkcieTabulkyContext);
  const zamok = useZamokTabulky();
  // Menu pásu na pravé tlačidlo — Zbaliť/Rozbaliť a Vložiť na koniec
  const [menuPasu, setMenuPasu] = useState<PolohaMenu | null>(null);

  // Viacvýber zostáv (KOLO 196) — keď je v ňom táto, akcia ide na všetky
  const cieleZostav = akcie?.cieleZostav(zostava) ?? [zostava];
  const viacZostav = cieleZostav.length > 1;

  async function handleDeleteZostava(): Promise<void> {
    if (viacZostav && akcie) {
      await akcie.zmazZostavy(cieleZostav);
      return;
    }
    if (!window.confirm(`Naozaj zmazať zostavu "${zostava.kod}"?`)) return;
    try {
      await deleteZostavaMutation.mutateAsync(zostava.id);
      toast({ title: "Zostava zmazaná" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDuplicateZostava(): Promise<void> {
    if (viacZostav && akcie) {
      await akcie.duplikujZostavy(cieleZostav);
      return;
    }
    try {
      // Duplikát ide hneď za zdroj — backend ho tam zaradí a prečísluje
      const newKod = pismena(
        existingZostavy.findIndex((z) => z.id === zostava.id) + 1,
      );
      await duplicateZostavaMutation.mutateAsync({
        id: zostava.id,
        data: { new_kod: newKod, new_nazov: `${zostava.nazov} (kópia)` },
      });
      toast({ title: "Zostava zduplikovaná", description: newKod });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri duplikovaní",
        description: extractErrorMessage(err),
      });
    }
  }

  // Duplikovať (KOLO 181) = kópia za ten istý riadok na serveri — nesie
  // všetky polia aj deti a je to jeden krok späť. Dovtedy sa skladala
  // na klientovi pole po poli, ticho strácala holý rozmer či hrany mimo
  // strán, išla na koniec zostavy a sendvič s dvomi vrstvami bol tri
  // kroky späť.
  async function duplikuj(d: Dielec): Promise<void> {
    if (!akcie) return;
    const r = await akcie.schranka.vlozRiadky({
      zostavaId: zostava.id,
      dielce: [d.id],
      zaDielecId: d.id,
      rezim: "kopia",
      popis: `duplikovanie dielca ${d.kod ?? ""}`.trim(),
    });
    const nove = r?.vlozene[0];
    if (!nove) return;
    onSelectDielec(nove);
    toast({ title: "Dielec zduplikovaný", description: "Kópia je hneď pod ním." });
  }

  /** Posunúť hore / dole z menu — to isté ako Alt+↑/↓ (KOLO 176). */
  function posunJeden(id: string, smer: -1 | 1): void {
    presunNa(posunOJeden(korenoveBezPausalu, posuvatelne, id, smer), id);
  }

  function mozePosunut(id: string): { hore: boolean; dole: boolean } {
    const pohyblive = korenoveBezPausalu.filter((x) => posuvatelne.has(x));
    const i = pohyblive.indexOf(id);
    return { hore: i > 0, dole: i >= 0 && i < pohyblive.length - 1 };
  }

  async function patchZostava(
    field: "nazov" | "popis" | "pocet_ks" | "mnozstvo_m",
    value: string,
  ): Promise<void> {
    try {
      const data: Record<string, string | number | null> = {};
      if (field === "pocet_ks") {
        data[field] = parseInt(value, 10) || 1;
      } else if (field === "mnozstvo_m") {
        // Metre (KOLO 227) — kusy pre výrobu dopočíta server
        if (!(toNum(value) > 0)) return;
        data.mnozstvo_m = parseDecimal(value);
      } else if (field === "popis") {
        // Popis sa dá aj zmazať — názov nie
        const novy = value.trim() || null;
        if (novy === (zostava.popis ?? null)) return;
        data.popis = novy;
      } else {
        if (!value.trim()) return;
        data[field] = value.trim();
      }
      await updateZostavaMutation.mutateAsync({ id: zostava.id, data });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  const nesieZostavu = (e: React.DragEvent): boolean =>
    Array.from(e.dataTransfer?.types ?? []).includes(ZOSTAVA_MIME);
  // Rozbalenú zostavu nasledujú jej dielce — „pod pás" by ťahanú vložilo
  // až za ne, kým čiara by svietila pod pásom. Rozbalený pás preto berie
  // len „nad"; na koniec sa dá dostať cez zbalenú poslednú alebo Alt+↓.
  const podPasom = (e: React.DragEvent<HTMLElement>): boolean => {
    if (expanded && dielceRows.length + operaciaRows.length > 0) return false;
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY > r.top + r.height / 2;
  };

  return (
    <>
      {/* Hlavička skupiny — klik otvorí detail produktu (marža, cena).
          Od KOLA 102 sú farby tokeny (`index.css`), takže sa dve verzie
          palety prepínajú bez vetvenia tu. Text a ikony majú vlastný
          token: vo „výraznej" verzii je pás tmavý a červená cena aj
          `muted-foreground` ikony by na ňom zanikli.
          Koľajnicu vľavo zostava NEMÁ — tá patrí položkám v nej. */}
      <TableRow
        onClick={onKlikZostava}
        onContextMenu={(e) => {
          if (jeVstup(e.target)) return;
          e.preventDefault();
          // Ako riadok dielca (KOLO 196): pravé na pás mimo výberu ho
          // vyberie, vo výbere ho nechá — akcia ide na celý výber
          if (!zostavaSelected) onKlikZostava?.(e);
          setMenuPasu({ x: e.clientX, y: e.clientY });
        }}
        onDragOver={
          tahanieZostavy
            ? (e) => {
                if (!nesieZostavu(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                tahanieZostavy.onNad(podPasom(e));
              }
            : undefined
        }
        onDragLeave={tahanieZostavy ? () => tahanieZostavy.onOdchod() : undefined}
        onDrop={
          tahanieZostavy
            ? (e) => {
                if (!nesieZostavu(e)) return;
                e.preventDefault();
                const id = e.dataTransfer.getData(ZOSTAVA_MIME);
                if (id) tahanieZostavy.onPustenie(id, podPasom(e));
              }
            : undefined
        }
        data-testid={`zostava-row-${zostava.id}`}
        aria-selected={zostavaSelected}
        className={cn(
          "group/pas bg-riadok-zostava hover:bg-riadok-zostava-hover text-riadok-zostava-text border-l-4 border-l-transparent font-medium",
          // Vzduch nad každou zostavou okrem prvej (KOLO 180) — skupiny
          // oddelí medzera, nie ďalšia čiara. `!` kvôli `TableBody`, ktorý
          // poslednému riadku tela zmaže všetky okraje: zbalená posledná
          // zostava (jej pás je posledný riadok) medzeru strácala (KOLO 183)
          "!border-t-[10px] border-t-background first:!border-t-0",
          "[&_button]:text-riadok-zostava-ikona",
          onKlikZostava && "cursor-pointer",
          // Vybraná zostava je celá modrá — dovtedy ružová so svetlými
          // ikonami, ktoré na nej zanikli (kontrast 1,1 : 1)
          zostavaSelected && "bg-vyber-zostava hover:bg-vyber-zostava",
        )}
        // Čiara, kam zostava pristane (KOLO 183) — kreslí ju `index.css`
        // v bunkách, tieň riadku by prekryla medzera nad pásom
        data-ciel={tahanieZostavy?.ciel ?? undefined}
      >
        <VyberCell
          zaskrtnute={zostavaVybrana}
          ciastocne={zostavaCiastocne}
          onToggle={onToggleZostavaVyber}
          label={`Vybrať dielce zostavy ${zostava.kod}`}
        />
        <TableCell className="p-1">
          {/* Ťahadlo zostavy (KOLO 183) — pri prejdení myšou po páse,
              ťahá sa len za neho (dvojklik na bunku ostáva úprava) */}
          {tahanieZostavy ? (
            <span
              draggable
              role="button"
              aria-label={`Posunúť zostavu ${zostava.kod}`}
              title="Potiahni a posuň zostavu (Alt+↑ / Alt+↓ na vybranej zostave)"
              onClick={(e) => e.stopPropagation()}
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.setData(ZOSTAVA_MIME, zostava.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => tahanieZostavy.onKoniec()}
              className="hidden cursor-grab justify-center text-riadok-zostava-ikona hover:text-white active:cursor-grabbing group-hover/pas:flex"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          ) : null}
        </TableCell>
        {/* Koreň stromu: chevron + písmeno zostavy (KOLO 134, 136) */}
        <StromBunka
          hlbka={0}
          rozbalitelne
          rozbalene={expanded}
          deti={dielceRows.length + operaciaRows.length > 0}
          onToggle={onToggle}
          toggleLabel={expanded ? "Zbaliť" : "Rozbaliť"}
          toggleTitle="Zbaliť / rozbaliť zostavu"
          className="font-mono text-sm font-bold tracking-wide"
          title={zostava.kod}
        >
          {/* Písmeno zostavy na dlaždici (KOLO 180) — hlavička skupiny
              sa dá nájsť očami aj v dlhej zákazke */}
          <span
            className={cn(
              "inline-grid h-6 min-w-[24px] place-items-center rounded-md bg-white px-1",
              zostavaSelected ? "text-vyber-zostava" : "text-riadok-zostava",
            )}
          >
            {zostava.kod}
          </span>
        </StromBunka>
        <InlineCell
          value={zostava.nazov}
          onSave={(v) => patchZostava("nazov", v)}
          mono={false}
          className="max-w-[200px] truncate"
          titul={zostava.nazov}
          label={`Názov zostavy ${zostava.kod}`}
        />
        <InlineCell
          value={zostava.popis ?? ""}
          onSave={(v) => patchZostava("popis", v)}
          mono={false}
          className="max-w-[180px] truncate text-sm font-normal"
          titul={zostava.popis ?? ""}
          label={`Popis zostavy ${zostava.kod}`}
        />
        {metreZostavy ? (
          <InlineCell
            value={formatCisloBezNul(zostava.mnozstvo_m)}
            display={`${formatCisloBezNul(zostava.mnozstvo_m)} m`}
            subtext={`${zostava.pocet_ks} ks`}
            onSave={(v) => patchZostava("mnozstvo_m", v)}
            textRight
            // „1,5 m" sa v úzkom stĺpci Ks lámalo na tri riadky
            className="whitespace-nowrap"
            label={`Metre zostavy ${zostava.kod}`}
          />
        ) : (
          <InlineCell
            value={String(zostava.pocet_ks ?? "")}
            onSave={(v) => patchZostava("pocet_ks", v)}
            textRight
            label={`Počet kusov zostavy ${zostava.kod}`}
          />
        )}
        <TableCell colSpan={4}>
          <div className="flex items-center justify-between gap-2">
            {/* Počet a cena v kapsulách (KOLO 180). Počet sú položky
                kusovníka — bez vrstiev sendviča, vnorených operácií,
                súčastí skla a paušálu (dovtedy rátal všetky riadky).
                Zbalená zostava má kapsulu svetlú so šípkou: „vnútri
                niečo je" (kolega: „keď je to zavreté, nech je vidno"). */}
            <span
              data-testid={`zostava-pocet-${zostava.id}`}
              className={cn(
                "whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs",
                !expanded
                  ? "bg-white font-semibold text-riadok-zostava"
                  : zostavaSelected
                    ? "bg-vyber-zostava-kapsula text-vyber-zostava-tlmeny"
                    : "bg-riadok-zostava-kapsula text-riadok-zostava-tlmeny",
              )}
            >
              {!expanded ? "▸ " : ""}
              {dielceRows.length}{" "}
              {sklonuj(dielceRows.length, ["dielec", "dielce", "dielcov"])}
            </span>
            {/* Cena za ks a celkom v jednom riadku vedľa seba (KOLO 229 —
                user: „nemusíš dať cenu do riadka pod, ale aj doprava"; do
                KOLA 229 celkom pod cenou za ks, KOLO 226); pri 1 ks je to
                to isté číslo, ostane jedno */}
            <span
              data-testid={`zostava-cena-${zostava.id}`}
              className={cn(
                "ml-auto flex items-baseline gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 leading-tight",
                zostavaSelected
                  ? "bg-vyber-zostava-kapsula text-white"
                  : "bg-riadok-zostava-kapsula text-riadok-zostava-cena",
              )}
              title={
                metreZostavy
                  ? `Cena za 1 m, vedľa celkom × ${formatMnozstvoZostavy(zostava)}`
                  : viacKusov
                    ? `Cena za 1 ks so ziskom, vedľa celkom × ${zostava.pocet_ks} ks`
                    : "Cena zostavy (so ziskom)"
              }
            >
              <span className="font-display text-sm font-semibold">
                {formatEur(
                  metreZostavy
                    ? zostava.cena_za_mj
                    : viacKusov
                      ? zostava.cena_zostavy_so_ziskom
                      : zostava.cena_celkom,
                )}
                {jednotkovaCena ? (
                  <span className="ml-0.5 text-[10px] font-normal">
                    {metreZostavy ? "/m" : "/ks"}
                  </span>
                ) : null}
              </span>
              {jednotkovaCena ? (
                <span className="text-[11px] font-medium">
                  · celkom {formatEur(zostava.cena_celkom)}
                </span>
              ) : null}
            </span>
          </div>
        </TableCell>
        {/* Päť ikon inline (KOLO 100 — reverz `⋮` z KOLA 99: popup
            prekrýval tri riadky tabuľky a bol neúmerný k tomu, čo ponúka).
            Od KOLA 181 sa bunka rozlieva cez Operácie, Nadmieru, Prírez,
            Materiál a ⋮ — 156 px ikon tak nenafúkne úzky stĺpec ⋮ dielcov
            (`table-layout: auto` počíta šírku z min-contentu). User:
            „ikonky v čiernom riadku sú dobré, tie tam môžu byť". */}
        <TableCell colSpan={5} className="px-1" onClick={(e) => e.stopPropagation()}>
          {menuPasu && akcie ? (
            <MenuRiadku
              poloha={menuPasu}
              onClose={() => setMenuPasu(null)}
              hlavicka={
                viacZostav
                  ? `${popisZostav(cieleZostav.length)} vo výbere`
                  : `Zostava ${zostava.kod} · ${zostava.nazov}`
              }
              zbalit={{
                rozbalene: expanded,
                label: expanded ? "Zbaliť zostavu" : "Rozbaliť zostavu",
                onClick: onToggle,
              }}
              kopirovat={{
                label: "Kopírovať",
                onClick: () => undefined,
                dovod: "kopírujú sa dielce — klikni pravým na dielec",
              }}
              vlozit={{
                label: "Vložiť na koniec zostavy",
                onClick: () => void akcie.schranka.vloz(zostava.id, null),
                dovod: akcie.schranka.moznost.ok ? null : akcie.schranka.moznost.dovod,
              }}
              vystrihnut={{
                label: "Vystrihnúť",
                onClick: () => undefined,
                dovod: "vystrihujú sa dielce — klikni pravým na dielec",
              }}
              hore={{
                label: "Posunúť zostavu hore",
                onClick: () => posunZostavy?.onPosun(-1),
                dovod: viacZostav
                  ? "posúva sa jedna zostava"
                  : dovodPosunuZostavy(posunZostavy, "hore"),
              }}
              dole={{
                label: "Posunúť zostavu dole",
                onClick: () => posunZostavy?.onPosun(1),
                dovod: viacZostav
                  ? "posúva sa jedna zostava"
                  : dovodPosunuZostavy(posunZostavy, "dole"),
              }}
            >
              <PolozkyPridatAZbalit zostavaId={zostava.id} pocetZostav={cieleZostav.length} />
              {/* Dať do celku ▸ (KOLO 235) — aj pre viac vybraných zostáv */}
              <OddelovacMenu />
              <PodmenuDoCelku
                celky={akcie.celky}
                aktualny={
                  new Set(cieleZostav.map((c) => c.celok_id ?? null)).size === 1
                    ? (cieleZostav[0]?.celok_id ?? null)
                    : undefined
                }
                onVyber={(celokId) => akcie.datDoCelku(cieleZostav, celokId)}
                onNovy={() => akcie.novyCelok(cieleZostav)}
              />
              {/* Akcie ikon na boku pásu aj v menu (KOLO 196) — user: „keď
                  pravým ťuknem na zostavu, tak nie je kópia, to musím
                  stlačiť tie tlačidlá, čo sú na boku" */}
              <OddelovacMenu />
              {/* Pri cieli v texte bez skratky — v menu w-52 by sa text orezal */}
              <PolozkaMenu
                ikona={Copy}
                skratka={viacZostav ? undefined : "Ctrl+D"}
                onSelect={() => void handleDuplicateZostava()}
              >
                {viacZostav ? `Duplikovať ${popisZostav(cieleZostav.length)}` : "Duplikovať zostavu"}
              </PolozkaMenu>
              <PolozkaMenu
                ikona={BookmarkPlus}
                citanie
                disabled={viacZostav}
                dovod="šablóna sa ukladá z jednej zostavy"
                onSelect={() => setSablonaDialogOpen(true)}
              >
                Uložiť ako šablónu
              </PolozkaMenu>
              <OddelovacMenu />
              <PolozkaMenu
                ikona={Trash2}
                nebezpecna
                skratka={viacZostav ? undefined : "Delete"}
                onSelect={() => void handleDeleteZostava()}
              >
                {viacZostav ? `Zmazať ${popisZostav(cieleZostav.length)}` : "Zmazať zostavu"}
              </PolozkaMenu>
            </MenuRiadku>
          ) : null}
          <div className="flex items-center justify-end gap-0.5">
            {/* Interná poznámka (KOLO 226) — len v appke, nikam sa neposiela.
                Od KOLA 229 s textom vo voľnom mieste pásu pred ikonami (user:
                „tá poznámka tam má miesto, že môžeš rozpísať text doprava");
                bunka pri počte je úzka, miesto je až tu. `w-0 flex-1`, aby
                dlhý text nerozťahoval stĺpce tabuľky. */}
            {zostava.poznamky?.trim() ? (
              <span
                role="img"
                aria-label={`Poznámka zostavy ${zostava.kod}: ${zostava.poznamky}`}
                title={zostava.poznamky}
                data-testid={`zostava-poznamka-${zostava.id}`}
                className={cn(
                  "mr-2 flex w-0 min-w-0 flex-1 items-center gap-1 text-xs",
                  zostavaSelected ? "text-white" : "text-amber-300",
                )}
              >
                <StickyNote className="h-4 w-4 shrink-0" />
                <span className="truncate">{zostava.poznamky.trim()}</span>
              </span>
            ) : null}
            {zamok ? null : (
            <Button
              size="icon"
              variant="ghost"
              onClick={onAddDielec}
              aria-label={`Pridať dielec do ${zostava.kod}`}
              title="Pridať dielec do zostavy"
              className="h-7 w-7"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            )}
            {onAddKovanie && !zamok ? (
              <Button
                size="icon"
                variant="ghost"
                onClick={onAddKovanie}
                aria-label={`Pridať kovanie do ${zostava.kod}`}
                title="Pridať kovanie"
                className="h-7 w-7"
              >
                <IconKusovy className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {zamok ? null : (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleDuplicateZostava}
              disabled={duplicateZostavaMutation.isPending}
              aria-label={`Duplikovať zostavu ${zostava.kod}`}
              title="Duplikovať zostavu vrátane dielcov"
              className="h-7 w-7"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setSablonaDialogOpen(true)}
              aria-label={`Uložiť zostavu ${zostava.kod} ako šablónu`}
              title="Uložiť ako šablónu — vložíš ju do inej zákazky"
              className="h-7 w-7"
            >
              <BookmarkPlus className="h-3.5 w-3.5" />
            </Button>
            <SablonaSaveDialog
              zostavaId={zostava.id}
              zostavaNazov={zostava.nazov}
              open={sablonaDialogOpen}
              onOpenChange={setSablonaDialogOpen}
            />
            {zamok ? null : (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleDeleteZostava}
              disabled={deleteZostavaMutation.isPending}
              aria-label={`Zmazať zostavu ${zostava.kod}`}
              title="Zmazať zostavu"
              className="h-7 w-7 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Dielce child rows (iba ak expanded); lepené vrstvy pod rodičom */}
      {expanded
        ? dielceRows.map((d, i) => {
            const vrstvy = vrstvyByParent.get(d.id) ?? [];
            // Strom (KOLO 136): pod zostavou idú dielce a za nimi paušál,
            // pod dielcom najprv operácie, potom vrstvy — v tom poradí sa
            // počíta, kto je posledný (└─)
            const posledny =
              i === dielceRows.length - 1 && operaciaRows.length === 0;
            const deti = [
              ...vrstvy.filter(jeOperaciaChild),
              ...vrstvy.filter(jeLaminatRiadok),
              ...vrstvy.filter((v) => v.operacia === OPERACIA_LEPENA_VRSTVA),
            ];
            const posledneDieta = deti[deti.length - 1]?.id;
            return (
              <Fragment key={d.id}>
                <DielecInlineRow
                  zakazkaId={zakazkaId}
                  rozmeryBezHran={rozmeryBezHran}
                  dielec={d}
                  sloty={sloty}
                  operacie={operacie}
                  nazvyDielcov={nazvyDielcov}
                  hrubky={hrubky}
                  splitRules={splitRules}
                  slot={
                    d.material_slot_id
                      ? slotById.get(d.material_slot_id) ?? null
                      : null
                  }
                  parny={i % 2 === 1}
                  stromPosledny={posledny}
                  vrstvy={vrstvy}
                  vrstvyExpanded={expandedVrstvy.has(d.id)}
                  onToggleVrstvy={() => onToggleVrstvyDielca(d.id)}
                  onExpandVrstvy={() => onExpandVrstvyDielca(d.id)}
                  selected={selectedDielecId === d.id}
                  onSelect={(e) => onKlikDielec(d.id, e)}
                  vybrany={vybrane.has(d.id)}
                  onPrepniVyber={
                    onPrepniVyber ? () => onPrepniVyber(d.id) : undefined
                  }
                  onDuplicate={() => void duplikuj(d)}
                  onPosun={posuvatelne.has(d.id) ? (smer) => posunJeden(d.id, smer) : undefined}
                  mozePosunut={mozePosunut(d.id)}
                  onZbalZostavu={onToggle}
                  autoEditNazov={focusDielecId === d.id}
                  onAutoEditDone={onFocusHandled}
                  tahanie={posuvatelne.has(d.id) && !zamok ? tahanieRiadku(d) : undefined}
                />
                {expandedVrstvy.has(d.id)
                  ? vrstvy.filter(jeOperaciaChild).map((v) => (
                      <OperaciaRow
                        key={v.id}
                        zakazkaId={zakazkaId}
                        dielec={v}
                        parent={d}
                        posledny={v.id === posledneDieta}
                        rodicPosledny={posledny}
                        selected={selectedDielecId === v.id}
                        onSelect={() => onSelectDielec(v.id)}
                        onZbal={() => onToggleVrstvyDielca(d.id)}
                        zbalLabel={`Zbaliť vrstvy a operácie ${d.kod ?? ""}`.trim()}
                        vlozZa={d.id}
                      />
                    ))
                  : null}
                {expandedVrstvy.has(d.id)
                  ? vrstvy.filter(jeLaminatRiadok).map((v) => (
                      <LaminatRow
                        key={v.id}
                        zakazkaId={zakazkaId}
                        dielec={v}
                        parent={d}
                        posledny={v.id === posledneDieta}
                        rodicPosledny={posledny}
                        selected={selectedDielecId === v.id}
                        onSelect={() => onSelectDielec(v.id)}
                        onZbal={() => onToggleVrstvyDielca(d.id)}
                      />
                    ))
                  : null}
                {expandedVrstvy.has(d.id)
                  ? vrstvy
                      .filter((v) => v.operacia === OPERACIA_LEPENA_VRSTVA)
                      .map((v) => (
                      <DielecInlineRow
                        key={v.id}
                        rozmeryBezHran={rozmeryBezHran}
                        zakazkaId={zakazkaId}
                        dielec={v}
                        sloty={sloty}
                        operacie={operacie}
                        nazvyDielcov={nazvyDielcov}
                        hrubky={hrubky}
                        splitRules={splitRules}
                        slot={
                          v.material_slot_id
                            ? slotById.get(v.material_slot_id) ?? null
                            : null
                        }
                        isVrstva
                        rodic={d}
                        stromPosledny={v.id === posledneDieta}
                        rodicPosledny={posledny}
                        selected={selectedDielecId === v.id}
                        onSelect={() => onSelectDielec(v.id)}
                        vybrany={false}
                        onDuplicate={() => undefined}
                        onToggleVrstvy={() => onToggleVrstvyDielca(d.id)}
                        vrstvyExpanded
                        autoEditNazov={false}
                      />
                    ))
                  : null}
              </Fragment>
            );
          })
        : null}

      {/* Operácie zostavy až pod dielcami — paušál je dodatok ku kusovníku */}
      {expanded
        ? operaciaRows.map((o, i) => (
            <OperaciaRow
              key={o.id}
              zakazkaId={zakazkaId}
              dielec={o}
              posledny={i === operaciaRows.length - 1}
              selected={selectedDielecId === o.id}
              onSelect={() => onSelectDielec(o.id)}
              onZbal={onToggle}
              zbalLabel={`Zbaliť zostavu ${zostava.kod}`}
              vlozZa={null}
            />
          ))
        : null}
    </>
  );
}

/** Riadok operácie: názov + ks (edit) + cena + zmazanie. */
function OperaciaRow({
  zakazkaId,
  dielec,
  parent,
  posledny = false,
  rodicPosledny = false,
  selected,
  onSelect,
  onZbal,
  zbalLabel,
  vlozZa,
}: {
  zakazkaId: string;
  dielec: Dielec;
  /** Posledný medzi súrodencami v strome (KOLO 136). */
  posledny?: boolean;
  /** Rodičovský dielec je posledný v zostave — kmeň zostavy tu končí. */
  rodicPosledny?: boolean;
  /**
   * Rodičovský dielec — z jeho rozmerov sa počíta množstvo operácie.
   * Chýba pri operáciách visiacich priamo na zostave („Ostatné").
   */
  parent?: Dielec;
  selected: boolean;
  onSelect: () => void;
  /** Zbaliť v menu (KOLO 181) — skupinu rodiča, paušál celú zostavu. */
  onZbal: () => void;
  zbalLabel: string;
  /** Vložiť z menu ide za tento koreňový riadok (`null` = koniec zostavy). */
  vlozZa: string | null;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);
  const akcie = useContext(AkcieTabulkyContext);
  const [menu, setMenu] = useState<PolohaMenu | null>(null);
  const vystrihnuty = parent != null && akcie?.schranka.vystrihnute.has(parent.id);
  const cena = dielec.cena_dielca ? parseFloat(dielec.cena_dielca) : null;
  // Operácia pod dielcom ide s jeho počtom (KOLO 193) — drží ho server,
  // tu sa berie z rodiča, aby po zmene počtu dielca nesvietil starý
  const ks = (parent ? parent.pocet_ks : dielec.pocet_ks) ?? 1;
  // výpočet viditeľný pre používateľa: množstvo z rodiča × cena z tabuľky operácií
  const opQuery = useOperacie();
  const op = (opQuery.data?.data ?? []).find((o) => o.kod === dielec.operacia);
  const opCena = op ? parseFloat(String(op.cena_eur_per_mj ?? "0")) || 0 : 0;
  // Bez rodiča (operácia zostavy) niet odkiaľ vziať plochu/dĺžku — vypíše sa
  // len množstvo v ks/hod, ostatné MJ zostanú bez rozpisu výpočtu.
  const px = parent?.x_mm ? parseFloat(parent.x_mm) : null;
  const py = parent?.y_mm ? parseFloat(parent.y_mm) : null;
  let vypocet: string | null = null;
  if (op && opCena > 0 && op.mj) {
    const mnozstvo =
      op.mj === "m2" && px && py
        ? `${normalizeNum((px * py) / 1e6)} m²`
        : op.mj === "m" && px
          ? `${normalizeNum(px / 1000)} m`
          : op.mj === "ks" || op.mj === "hod"
            ? `1 ${op.mj}`
            : null;
    if (mnozstvo) {
      const rezia = parseFloat(String(op.rezia_pct ?? "0")) || 0;
      // Lepenie sendviča: 4 vrstvy = 3 škáry (KOLO 226) — cena ich už
      // násobí, bez textu by riadok vyzeral ako jedno lepenie
      const skary =
        dielec.lepenie_skary != null && dielec.lepenie_skary > 1
          ? ` × ${dielec.lepenie_skary} škáry`
          : "";
      vypocet = `${mnozstvo}${skary} × ${formatEur(opCena)}/${op.mj}${rezia > 0 ? ` + réžia ${normalizeNum(rezia)} %` : ""}`;
    }
  }
  const bezCeny = op != null && (opCena <= 0 || !op.mj);
  return (
    <TableRow
      onClick={onSelect}
      onContextMenu={(e) => {
        if (jeVstup(e.target)) return;
        e.preventDefault();
        if (!selected) onSelect();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
      className={cn(
        // Podriadok: vlastný odtieň. Od lepenej vrstvy ho odlišuje ikona,
        // kurzíva a odsadenie (KOLO 100, tokeny KOLO 102). Vnorená
        // operácia má navyše sivý pruh skupiny pod rodičom (KOLO 180),
        // paušál zostavy nie — patrí zostave, nie dielcu.
        "group/riadok cursor-pointer border-l-4 border-l-transparent bg-riadok-podriadok text-riadok-podriadok-text hover:bg-riadok-podriadok-hover",
        parent && "border-l-tab-vnorene",
        posledny && parent && "!border-b-tab-vnorene",
        selected && VYBRANY_RIADOK,
        vystrihnuty && "opacity-50",
      )}
      data-testid={`operacia-row-${dielec.id}`}
      aria-selected={selected}
    >
      {/* Operačný riadok sa do hromadnej úpravy nedá vybrať — nemá slot
          ani hrany, takže by preň nebolo čo nastaviť. */}
      <VyberCell />
      <TableCell className="p-1">
        <span
          role="img"
          aria-label={`Operácia ${dielec.kod}`}
          title={
            parent
              ? "Vnorená operácia — množstvo z rozmerov dielca"
              : "Operácia zostavy — účtuje sa za kus, nie z rozmerov"
          }
          className="flex justify-center"
        >
          {/* Modrá, nie červená — červená ostáva chybe (KOLO 180) */}
          <IconOperacia className="h-3 w-3 text-vyber" />
        </span>
      </TableCell>
      {/* Operačný riadok (paušál, vnorená operácia) sa nečísluje — jeho
          interný kód (`A_ostatne`) by v stĺpci ID len mätol (KOLO 134);
          v strome má len vetvu (KOLO 136) */}
      <StromBunka
        hlbka={parent ? 2 : 1}
        posledny={posledny}
        rodicPosledny={rodicPosledny}
      />
      <TableCell
        className={cn(
          "max-w-[200px] truncate text-xs italic",
          parent && ODSADENY_NAZOV,
        )}
        title={dielec.nazov_strojovy ?? dielec.operacia ?? ""}
      >
        {dielec.nazov_strojovy ?? dielec.operacia}
        {dielec.lepenie_skary != null ? (
          <span
            className="ml-1.5 not-italic font-medium"
            title={`Sendvič s ${dielec.lepenie_skary + 1} vrstvami sa lepí ${dielec.lepenie_skary}×`}
          >
            {dielec.lepenie_skary}×
          </span>
        ) : null}
        <span className="ml-1.5 font-mono text-[10px] not-italic text-muted-foreground">
          {dielec.operacia}
        </span>
        {vypocet ? (
          <span className="block font-mono text-[10px] not-italic text-muted-foreground">
            {vypocet}
          </span>
        ) : null}
      </TableCell>
      {/* Popis operácia nemá */}
      <TableCell />
      {parent ? (
        <TableCell
          className="text-right font-sans text-sm"
          aria-label={`Počet ks operácie ${dielec.kod}`}
          title={`Počet ide s dielcom ${parent.kod ?? ""} — zmeň počet dielca`}
        >
          {ks}
        </TableCell>
      ) : (
        <InlineCell
          value={String(ks)}
          onSave={(v) => {
            const n = Math.max(1, parseInt(v, 10) || 1);
            updateMutation.mutate({ id: dielec.id, data: { pocet_ks: n } });
          }}
          textRight
          label={`Počet ks operácie ${dielec.kod}`}
        />
      )}
      <TableCell colSpan={7} className="text-right font-mono text-[11px]">
        {cena != null && cena > 0 ? (
          formatEur(cena * ks)
        ) : bezCeny ? (
          <span
            className="text-amber-600"
            title="Operácia nemá nastavenú cenu — nastav ju v Cenníku → Operácie"
          >
            bez ceny
          </span>
        ) : (
          ""
        )}
      </TableCell>
      <TableCell />
      <TableCell className="p-1 text-right">
        <TlacidloMenu kod={dielec.kod} onOtvor={setMenu} />
        {menu && akcie ? (
          <MenuRiadku
            poloha={menu}
            onClose={() => setMenu(null)}
            hlavicka={`${parent ? `${parent.kod} · ` : ""}${dielec.nazov_strojovy ?? dielec.operacia ?? ""}`}
            zbalit={{ rozbalene: true, label: zbalLabel, onClick: onZbal }}
            kopirovat={{
              label: "Kopírovať",
              onClick: () => undefined,
              dovod: parent
                ? "operácia ide s dielcom — kopíruj celý dielec"
                : "paušál má každá zostava svoj",
            }}
            vlozit={{
              label: parent ? `Vložiť za ${parent.kod}` : "Vložiť na koniec zostavy",
              onClick: () => void akcie.schranka.vloz(dielec.zostava_id, vlozZa),
              dovod: akcie.schranka.moznost.ok ? null : akcie.schranka.moznost.dovod,
            }}
            vystrihnut={{
              label: "Vystrihnúť",
              onClick: () => undefined,
              dovod: parent
                ? "operácia ide s dielcom — vystrihni celý dielec"
                : "paušál má každá zostava svoj",
            }}
            hore={{
              label: "Posunúť hore",
              onClick: () => undefined,
              dovod: parent ? "operácia ide s dielcom" : "paušál ostáva na konci zostavy",
            }}
            dole={{
              label: "Posunúť dole",
              onClick: () => undefined,
              dovod: parent ? "operácia ide s dielcom" : "paušál ostáva na konci zostavy",
            }}
          >
            <PolozkyPridatAZbalit zostavaId={dielec.zostava_id} />
            <OddelovacMenu />
            <PolozkaMenu
              ikona={Trash2}
              nebezpecna
              onSelect={() => void deleteMutation.mutateAsync(dielec.id)}
            >
              Zmazať operáciu
            </PolozkaMenu>
          </MenuRiadku>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * Riadok laminátu pod doskou (KOLO 234). Rozmer, počet a nadmieru drží
 * server podľa dosky — riadok ukazuje laminát, prírez (+20 / +20), počet
 * a cenu; v menu výmena laminátu (len za iný laminát) a zmazanie.
 */
function LaminatRow({
  zakazkaId,
  dielec,
  parent,
  posledny,
  rodicPosledny,
  selected,
  onSelect,
  onZbal,
}: {
  zakazkaId: string;
  dielec: Dielec;
  parent: Dielec;
  posledny: boolean;
  rodicPosledny: boolean;
  selected: boolean;
  onSelect: () => void;
  onZbal: () => void;
}): JSX.Element {
  const deleteMutation = useDeleteDielec(zakazkaId);
  const updateMutation = useUpdateDielec(zakazkaId);
  const akcie = useContext(AkcieTabulkyContext);
  const [menu, setMenu] = useState<PolohaMenu | null>(null);
  const [vymena, setVymena] = useState(false);
  const ks = parent.pocet_ks ?? dielec.pocet_ks ?? 1;
  const cena = dielec.cena_dielca ? parseFloat(dielec.cena_dielca) : null;
  const m = dielec.material_direct;
  const kod = m ? (m.vlastny_id ?? m.demos_id ?? m.jafholz_id ?? m.nazov) : "?";
  const px = dielec.x_mm ? parseFloat(dielec.x_mm) : null;
  const py = dielec.y_mm ? parseFloat(dielec.y_mm) : null;
  const nx = dielec.nadmiera_x_mm ? parseFloat(dielec.nadmiera_x_mm) : 0;
  const ny = dielec.nadmiera_y_mm ? parseFloat(dielec.nadmiera_y_mm) : 0;
  const prirez = px && py ? `${normalizeNum(px + nx)} × ${normalizeNum(py + ny)}` : "—";
  return (
    <TableRow
      onClick={onSelect}
      onContextMenu={(e) => {
        if (jeVstup(e.target)) return;
        e.preventDefault();
        if (!selected) onSelect();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
      className={cn(
        "group/riadok cursor-pointer border-l-4 border-l-tab-vnorene bg-riadok-podriadok text-riadok-podriadok-text hover:bg-riadok-podriadok-hover",
        posledny && "!border-b-tab-vnorene",
        selected && VYBRANY_RIADOK,
      )}
      data-testid={`laminat-row-${dielec.id}`}
      aria-selected={selected}
    >
      <VyberCell />
      <TableCell className="p-1">
        <span
          role="img"
          aria-label={`Laminát ${parent.kod ?? ""}`.trim()}
          title="Laminát na dosku — rozmer a počet drží doska"
          className="flex justify-center"
        >
          <Layers className="h-3 w-3 text-vyber" />
        </span>
      </TableCell>
      <StromBunka hlbka={2} posledny={posledny} rodicPosledny={rodicPosledny} />
      <TableCell
        className={cn("max-w-[260px] truncate text-xs", ODSADENY_NAZOV)}
        title={m?.nazov ?? ""}
      >
        {dielec.nazov_strojovy ?? "laminát"}
        <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">{kod}</span>
      </TableCell>
      <TableCell />
      <TableCell
        className="text-right font-sans text-sm"
        aria-label={`Počet ks laminátu ${dielec.kod}`}
        title={`Počet ide s doskou ${parent.kod ?? ""} — zmeň počet dosky`}
      >
        {ks}
      </TableCell>
      <TableCell colSpan={7} className="font-mono text-[11px]">
        <span className="flex items-center justify-between gap-2">
          <span
            className="text-muted-foreground"
            title="Prírez laminátu = rozmer dosky + nadmiera 20 / 20 (Cenník → Operácie → Laminovanie)"
          >
            prírez {prirez}
          </span>
          <span>{cena != null && cena > 0 ? formatEur(cena * ks) : ""}</span>
        </span>
      </TableCell>
      <TableCell />
      <TableCell className="p-1 text-right">
        <TlacidloMenu kod={dielec.kod} onOtvor={setMenu} />
        {menu && akcie ? (
          <MenuRiadku
            poloha={menu}
            onClose={() => setMenu(null)}
            hlavicka={`${parent.kod ?? ""} · ${dielec.nazov_strojovy ?? "laminát"}`}
            zbalit={{ rozbalene: true, label: `Zbaliť ${parent.kod ?? ""}`.trim(), onClick: onZbal }}
            kopirovat={{ label: "Kopírovať", onClick: () => undefined, dovod: "laminát ide s doskou — kopíruj dosku" }}
            vlozit={{
              label: `Vložiť za ${parent.kod ?? ""}`.trim(),
              onClick: () => void akcie.schranka.vloz(dielec.zostava_id, parent.id),
              dovod: akcie.schranka.moznost.ok ? null : akcie.schranka.moznost.dovod,
            }}
            vystrihnut={{ label: "Vystrihnúť", onClick: () => undefined, dovod: "laminát ide s doskou" }}
            hore={{ label: "Posunúť hore", onClick: () => undefined, dovod: "laminát ide s doskou" }}
            dole={{ label: "Posunúť dole", onClick: () => undefined, dovod: "laminát ide s doskou" }}
          >
            <PolozkaMenu ikona={Layers} onSelect={() => setVymena(true)}>
              Vymeniť laminát…
            </PolozkaMenu>
            <OddelovacMenu />
            <PolozkaMenu
              ikona={Trash2}
              nebezpecna
              onSelect={() => void deleteMutation.mutateAsync(dielec.id)}
            >
              Zmazať laminát
            </PolozkaMenu>
          </MenuRiadku>
        ) : null}
        {vymena ? (
          <VyberLaminatuDialog
            nadpis={`Vymeniť laminát — ${parent.kod ?? ""} ${dielec.nazov_strojovy ?? ""}`.trim()}
            onClose={() => setVymena(false)}
            onVyber={(id) => {
              setVymena(false);
              updateMutation.mutate({ id: dielec.id, data: { material_id_direct: id } });
            }}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/**
 * Vybraný riadok (KOLO 180, návrh D): modrý podklad, 4 px modrý pruh
 * a tenký rám — vidno ho aj z diaľky a nemýli sa s červenou chybou.
 * Rám je vnútorný tieň (ako čiara ťahania z KOLA 176), `outline` by
 * pri `border-collapse` prekryl susedné riadky.
 */
const VYBRANY_RIADOK =
  "border-l-vyber bg-vyber-podklad hover:bg-vyber-podklad [&_td]:shadow-[inset_0_1px_0_0_hsl(var(--vyber)),inset_0_-1px_0_0_hsl(var(--vyber))]";

/**
 * Názov vnoreného riadku (vrstva sendviča, operácia pod dielcom) je
 * odsadený so šípkou ↳ (KOLO 180) — úroveň nižšie vidno aj mimo
 * stĺpca ID, kde bol doteraz jediný náznak (vetva stromu).
 */
const ODSADENY_NAZOV =
  "relative pl-8 before:absolute before:left-3 before:text-tab-strom before:not-italic before:content-['↳']";

/**
 * Štítok upravenej ceny riadku (KOLO 227): „cena −10 %" / „ručná cena".
 * Upravuje sa v detaile zostavy (rozpis ceny), tu je len vidieť, že
 * riadok nemá vypočítanú cenu.
 */
function StitokUpravyCeny({ dielec }: { dielec: Dielec }): JSX.Element | null {
  const zlava = dielec.zlava_pct != null && Number(dielec.zlava_pct) !== 0;
  if (dielec.cena_rucna == null && !zlava) return null;
  const text =
    dielec.cena_rucna != null
      ? `ručná cena ${formatEur(dielec.cena_rucna)}`
      : `cena −${formatCisloBezNul(dielec.zlava_pct)} %`;
  return (
    <span
      data-testid={`uprava-ceny-${dielec.id}`}
      title={`Vypočítaná cena ${formatEur(dielec.cena_vypocitana)} / ks — upravíš v detaile zostavy`}
      className="ml-1 whitespace-nowrap rounded bg-amber-100 px-1 text-[10px] text-amber-900"
    >
      {text}
    </span>
  );
}

/**
 * Kapsula „▸ 2 vrstvy · 1 operácia" pri dielci s deťmi (KOLO 180).
 * Zbalená je plná a tmavá — vidno, že pod dielcom niečo je; rozbalená
 * len obrys. Klik prepne to isté ako šípka v strome.
 */
function KapsulaDeti({
  kod,
  vrstvy,
  operacie,
  laminaty = 0,
  rozbalene,
  onToggle,
}: {
  kod: string;
  vrstvy: number;
  operacie: number;
  /** Riadky laminátu pod doskou (KOLO 234). */
  laminaty?: number;
  rozbalene: boolean;
  onToggle?: () => void;
}): JSX.Element {
  const text = [
    vrstvy > 0 ? `${vrstvy} ${sklonuj(vrstvy, ["vrstva", "vrstvy", "vrstiev"])}` : null,
    laminaty > 0
      ? `${laminaty} ${sklonuj(laminaty, ["laminát", "lamináty", "laminátov"])}`
      : null,
    operacie > 0
      ? `${operacie} ${sklonuj(operacie, ["operácia", "operácie", "operácií"])}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      aria-expanded={rozbalene}
      aria-label={`${rozbalene ? "Zbaliť" : "Rozbaliť"} ${text} pod ${kod}`}
      title={rozbalene ? "Zbaliť" : "Rozbaliť — pod dielcom sú skryté riadky"}
      className={cn(
        "inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border py-0.5 pl-1 pr-2 text-[11px] font-semibold",
        rozbalene
          ? "border-tab-vnorene text-riadok-podriadok-text hover:bg-riadok-podriadok"
          : "border-transparent bg-riadok-podriadok-text text-white hover:bg-tab-hlavicka-text",
      )}
    >
      {rozbalene ? (
        <ChevronDown className="h-3 w-3" />
      ) : (
        <ChevronRight className="h-3 w-3" />
      )}
      {text}
    </button>
  );
}

/**
 * ⋮ na konci riadku (KOLO 181, návrh A2) — otvorí to isté menu ako pravé
 * tlačidlo. Je stále viditeľné (tlmené): na dotykovom displeji pravé
 * tlačidlo nie je a ikony len pri myši z KOLA 180 tam nebolo vidno.
 */
function TlacidloMenu({
  kod,
  onOtvor,
}: {
  kod: string | null | undefined;
  onOtvor: (p: PolohaMenu) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={`Akcie ${kod ?? ""}`.trim()}
      aria-haspopup="menu"
      title="Akcie riadku — alebo pravé tlačidlo na riadku"
      onClick={(e) => {
        e.stopPropagation();
        onOtvor(polohaZTlacidla(e.currentTarget));
      }}
      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground"
    >
      <EllipsisVertical className="h-4 w-4" />
    </button>
  );
}

/**
 * Ako sa slot volá v stĺpci Materiál (KOLO 137): **kód materiálu
 * z cenníka** (`H1145_ST10_18`) a za ním názov — user: *„namiesto M1
 * dávaj ID H1145…"*. Je to ten istý kód, ktorý ide do Cut Rite aj do
 * podkladu pre pílu (`material_kod` v backende). Materiál bez kódu
 * ostane po starom `M1 názov`.
 */
/**
 * V stĺpci Materiál len kód dosky (KOLO 195) — user: *„uprav, aby
 * materiály mali iba ID, tým vznikne viac miesta na operácie"*. Celé
 * znenie je v `title` (`popisSlotu`). Doska bez kódu ukáže názov.
 */
function nazovSlotu(s: MaterialSlot): string {
  if (!s.material) return s.slot_kod;
  return materialPrimaryId(s.material) ?? s.material.nazov;
}

/** Plné znenie do `title` — slot, kód aj názov. */
function popisSlotu(s: MaterialSlot): string {
  if (!s.material) return s.slot_kod;
  const kod = materialPrimaryId(s.material);
  return [s.slot_kod, kod, s.material.nazov].filter(Boolean).join(" · ");
}

/**
 * Menu tabuľky — pravé tlačidlo mimo riadkov (KOLO 196): pridať zostavu na
 * koniec, vložiť a pridať do vybranej zostavy (inak poslednej), zbaliť
 * všetko. Kopírovať a posúvať sa dá len riadok či zostavu.
 */
function MenuTabulky({
  poloha,
  onClose,
  ciel,
  pocetZostav,
}: {
  poloha: PolohaMenu;
  onClose: () => void;
  ciel: Zostava | null;
  pocetZostav: number;
}): JSX.Element | null {
  const akcie = useContext(AkcieTabulkyContext);
  if (!akcie) return null;
  const { pridajKovanie, pridajCenovku, pridajVlastnu, pridajZostavu } = akcie;
  const doKoho = !ciel ? "" : pocetZostav > 1 ? ` do ${pocetZostav} zostáv` : ` do ${ciel.kod}`;
  const lenRiadok = "kopíruje a posúva sa riadok alebo zostava — klikni pravým na ne";
  return (
    <MenuRiadku
      poloha={poloha}
      onClose={onClose}
      hlavicka="Tabuľka"
      zbalit={{
        rozbalene: akcie.vsetkoRozbalene,
        label: akcie.vsetkoRozbalene ? "Zbaliť všetko" : "Rozbaliť všetko",
        onClick: akcie.prepniVsetko,
      }}
      kopirovat={{ label: "Kopírovať", onClick: () => undefined, dovod: lenRiadok }}
      vlozit={{
        label: ciel ? `Vložiť na koniec ${ciel.kod}` : "Vložiť",
        onClick: () => {
          if (ciel) void akcie.schranka.vloz(ciel.id, null);
        },
        dovod: !ciel
          ? "najprv pridaj zostavu"
          : akcie.schranka.moznost.ok
            ? null
            : akcie.schranka.moznost.dovod,
      }}
      vystrihnut={{ label: "Vystrihnúť", onClick: () => undefined, dovod: lenRiadok }}
      hore={{ label: "Posunúť hore", onClick: () => undefined, dovod: lenRiadok }}
      dole={{ label: "Posunúť dole", onClick: () => undefined, dovod: lenRiadok }}
    >
      {pridajZostavu ? (
        <PolozkaMenu ikona={ListPlus} skratka="Ctrl+Shift+Z" onSelect={() => pridajZostavu(null)}>
          Pridať zostavu
        </PolozkaMenu>
      ) : null}
      {/* Nový celok (KOLO 235) — nadpis nad zostavami */}
      <PolozkaMenu ikona={FolderPlus} onSelect={() => akcie.novyCelok([])}>
        Nový celok…
      </PolozkaMenu>
      {ciel ? (
        <>
          <PolozkaMenu ikona={Plus} onSelect={() => akcie.pridajDielec(ciel.id)}>
            Pridať dielec{doKoho}
          </PolozkaMenu>
          {pridajKovanie ? (
            <PolozkaMenu ikona={IconKusovy} onSelect={() => pridajKovanie(ciel.id)}>
              Pridať kovanie{doKoho}
            </PolozkaMenu>
          ) : null}
          {pridajCenovku ? (
            <PolozkaMenu ikona={Tag} onSelect={() => pridajCenovku(ciel.id)}>
              Pridať cenovku{doKoho}
            </PolozkaMenu>
          ) : null}
          {pridajVlastnu ? (
            <PolozkaMenu ikona={PackagePlus} onSelect={() => pridajVlastnu(ciel.id)}>
              Vlastná položka{doKoho}
            </PolozkaMenu>
          ) : null}
        </>
      ) : null}
    </MenuRiadku>
  );
}

/**
 * Pridať dielec / kovanie do zostavy riadku a Zbaliť všetko (KOLO 184) —
 * v menu dielca, operácie aj pásu zostavy. User: *„nech to pravé
 * tlačidlo obsahuje aj zbaliť všetko a pridať kovanie a dielec"*.
 * Dielec ide na koniec zostavy ako „+" na páse.
 */
function PolozkyPridatAZbalit({
  zostavaId,
  pocetZostav = 1,
  bezZbalenia = false,
}: {
  zostavaId: string;
  /** Viacvýber zostáv (KOLO 196) — dielec a kovanie idú do každej. */
  pocetZostav?: number;
  /** Menu dielca má Zbaliť všetko nižšie, za hranami. */
  bezZbalenia?: boolean;
}): JSX.Element | null {
  const akcie = useContext(AkcieTabulkyContext);
  if (!akcie) return null;
  const { pridajKovanie, pridajCenovku, pridajVlastnu, pridajZostavu } = akcie;
  const doKoho = pocetZostav > 1 ? ` do ${pocetZostav} zostáv` : "";
  const kod = akcie.kodZostavy(zostavaId);
  return (
    <>
      <PolozkaMenu
        ikona={Plus}
        skratka={doKoho ? undefined : "Ctrl+Enter"}
        onSelect={() => akcie.pridajDielec(zostavaId)}
      >
        Pridať dielec{doKoho}
      </PolozkaMenu>
      {pridajKovanie ? (
        <PolozkaMenu ikona={IconKusovy} onSelect={() => pridajKovanie(zostavaId)}>
          Pridať kovanie{doKoho}
        </PolozkaMenu>
      ) : null}
      {pridajCenovku ? (
        <PolozkaMenu ikona={Tag} onSelect={() => pridajCenovku(zostavaId)}>
          Pridať cenovku{doKoho}
        </PolozkaMenu>
      ) : null}
      {pridajVlastnu ? (
        <PolozkaMenu ikona={PackagePlus} onSelect={() => pridajVlastnu(zostavaId)}>
          Vlastná položka{doKoho}
        </PolozkaMenu>
      ) : null}
      {/* Zostava sa dala pridať len horným tlačidlom (KOLO 196) — z menu
          ide hneď pod zostavu riadku */}
      {pridajZostavu ? (
        <PolozkaMenu ikona={ListPlus} onSelect={() => pridajZostavu(zostavaId)}>
          {kod ? `Pridať zostavu pod ${kod}` : "Pridať zostavu"}
        </PolozkaMenu>
      ) : null}
      {bezZbalenia ? null : <PolozkaZbalitVsetko />}
    </>
  );
}

/** Zbaliť / rozbaliť všetky zostavy aj vrstvy — to isté ako ⇕ nad ID. */
function PolozkaZbalitVsetko(): JSX.Element | null {
  const akcie = useContext(AkcieTabulkyContext);
  if (!akcie) return null;
  return (
    <PolozkaMenu
      ikona={akcie.vsetkoRozbalene ? ChevronsDownUp : ChevronsUpDown}
      citanie
      onSelect={akcie.prepniVsetko}
    >
      {akcie.vsetkoRozbalene ? "Zbaliť všetko" : "Rozbaliť všetko"}
    </PolozkaMenu>
  );
}

function DielecInlineRow({
  zakazkaId,
  rozmeryBezHran = false,
  dielec,
  sloty,
  operacie,
  nazvyDielcov,
  hrubky,
  splitRules,
  slot,
  vrstvy = [],
  vrstvyExpanded = false,
  onToggleVrstvy,
  onExpandVrstvy,
  isVrstva = false,
  rodic,
  parny = false,
  stromPosledny = false,
  rodicPosledny = false,
  selected,
  onSelect,
  vybrany = false,
  onPrepniVyber,
  onDuplicate,
  onPosun,
  mozePosunut,
  onZbalZostavu,
  autoEditNazov,
  onAutoEditDone,
  tahanie,
}: {
  zakazkaId: string;
  /** Prepínač zákazky „rozmery bez hrán" (KOLO 131). */
  rozmeryBezHran?: boolean;
  dielec: Dielec;
  sloty: MaterialSlot[];
  operacie: Operation[];
  nazvyDielcov: NazovDielca[];
  hrubky: DostupnaHrubka[];
  splitRules: SplitRule[];
  slot: MaterialSlot | null;
  /** Lepené vrstvy tohto dielca (dielec je rodič). */
  vrstvy?: Dielec[];
  vrstvyExpanded?: boolean;
  onToggleVrstvy?: () => void;
  onExpandVrstvy?: () => void;
  /** Tento riadok JE vrstva (_lepN) — odsadený, bez split dialógu. */
  isVrstva?: boolean;
  /** Rodič vrstvy — vrstva sa ukazuje v jeho holom rozmere (KOLO 138). */
  rodic?: Dielec;
  /** Nepárny index medzi dielcami zostavy — zebra (KOLO 102). */
  parny?: boolean;
  /** Posledný medzi súrodencami v strome — vetva končí └─ (KOLO 136). */
  stromPosledny?: boolean;
  /** Pri vrstve: rodič je posledný v zostave — kmeň zostavy tu končí. */
  rodicPosledny?: boolean;
  selected: boolean;
  /** Klik na riadok — modifikátory (Shift/Ctrl) rieši volajúci. */
  onSelect: (e: React.MouseEvent) => void;
  /** Riadok je v hromadnom výbere. */
  vybrany?: boolean;
  /** Prepnutie vo výbere; `undefined` = riadok sa vyberať nedá (vrstva). */
  onPrepniVyber?: () => void;
  onDuplicate: () => void;
  /** Posunúť hore / dole z menu (KOLO 181); `undefined` = neposúva sa. */
  onPosun?: (smer: -1 | 1) => void;
  mozePosunut?: { hore: boolean; dole: boolean };
  /** Zbaliť v menu pri dielci bez detí — zbalí jeho zostavu. */
  onZbalZostavu?: () => void;
  autoEditNazov: boolean;
  onAutoEditDone?: () => void;
  /** Posúvanie ťahaním (KOLO 176); `undefined` = riadok sa neťahá. */
  tahanie?: TahanieRiadku;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const deleteMutation = useDeleteDielec(zakazkaId);
  const akcie = useContext(AkcieTabulkyContext);
  const zamok = useZamokTabulky();
  const schrankaHran = useSchrankaHran();
  const [menu, setMenu] = useState<PolohaMenu | null>(null);
  // CNC prírez — indikátor v stĺpci Nadmiera, keď sa dielec nezmestí na
  // stroj (predrez / rotácia / združenie). Zrkadlo backend pravidla.
  const gnCnc = useNastaveniaGlobal().data?.data;
  // Vybraný výrobný postup — z neho ide `je_cnc` do `cncStav`. Hádať to
  // z kódu sa nesmie: `lep_nc_olep_nc` obsahuje podreťazec `olep_nc`,
  // takže by tabuľka ukazovala iný prírez, než sa reže (KOLO 108).
  const postup = dielec.operacia
    ? (operacie.find((o) => o.kod === dielec.operacia) ?? null)
    : null;
  const cncRiadok =
    gnCnc && dielec.x_mm != null && dielec.y_mm != null
      ? cncStav({
          xMm: Number(dielec.x_mm),
          yMm: Number(dielec.y_mm),
          nadmieraXMm: Number(dielec.nadmiera_x_mm ?? 0),
          nadmieraYMm: Number(dielec.nadmiera_y_mm ?? 0),
          nadmieraAktivna: dielec.nadmiera_aktivna ?? true,
          pocet: dielec.pocet_ks ?? 1,
          operaciaJeCnc: postup?.je_cnc ?? null,
          nazovStrojovy: dielec.nazov_strojovy,
          cncRotovat: dielec.cnc_rotovat,
          ideNaCnc: dielec.ide_na_cnc,
          rezXMm: dielec.rez_x_mm != null ? Number(dielec.rez_x_mm) : null,
          rezYMm: dielec.rez_y_mm != null ? Number(dielec.rez_y_mm) : null,
          cnc: {
            minDlzkaMm: gnCnc.cnc_upnutie_min_dlzka_mm ?? 0,
            minSirkaMm: gnCnc.cnc_upnutie_min_sirka_mm ?? 0,
            zdruzenieMedzeraMm: gnCnc.cnc_zdruzenie_medzera_mm ?? 10,
            rotaciaMaxMm: gnCnc.cnc_rotacia_max_mm ?? 0,
            nazvy: {
              rez: gnCnc.cnc_nazov_rez,
              zdruz_rez: gnCnc.cnc_nazov_zdruz_rez,
              rot_rez: gnCnc.cnc_nazov_rot_rez,
              rot_zdruz_rez: gnCnc.cnc_nazov_rot_zdruz_rez,
            },
          },
        })
      : null;
  // Zobraz ikonu len keď sa dielec reálne zväčšuje (predrez/rotácia/združ.)
  const cncVarovanie =
    cncRiadok != null &&
    cncRiadok.cncDielec &&
    cncRiadok.dovod !== "ziadny" &&
    cncRiadok.dovod !== "nadmiera";
  // Zadanie lepenej hrúbky do Z → dialóg konverzie na vrstvy
  const [lepeny, setLepeny] = useState<{ rule: SplitRule; z: string } | null>(null);
  const [opDialogOpen, setOpDialogOpen] = useState(false);
  const [prirezOpen, setPrirezOpen] = useState(false);
  // Kovanie: klik na materiál → dialóg zmeny kovania
  const [zmenaKovania, setZmenaKovania] = useState(false);

  async function patch(
    data: Record<string, string | number | boolean | null>,
  ): Promise<void> {
    try {
      await updateMutation.mutateAsync({ id: dielec.id, data });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  // Desatinné množstvo vlastnej položky s čiarkou, ako ho user píše
  const mnozstvoVlastnej = normalizeNum(
    dielec.vlastna_mnozstvo ?? dielec.pocet_ks ?? 1,
  ).replace(".", ",");

  async function ulozMnozstvoVlastnej(value: string): Promise<void> {
    const nove = parseDecimal(value);
    if (!nove || Number(nove) === Number(parseDecimal(mnozstvoVlastnej))) return;
    await patch({ vlastna_mnozstvo: nove });
  }

  async function patchField(
    field:
      | "nazov_strojovy"
      | "nazov_volny"
      | "pocet_ks"
      | "x_mm"
      | "y_mm"
      | "z_mm",
    value: string,
  ): Promise<void> {
    if (field === "x_mm" || field === "y_mm") {
      // Bunka ukazuje holý rozmer, keď ho dielec má — porovnáva sa s ním
      const zobrazeny = zobrazenyRozmer(rozmery, field);
      if (value === (zobrazeny != null ? normalizeNum(zobrazeny) : "")) return;
      await patch(
        rozmerNaUlozenie(field, value || null, {
          bezHran: rozmeryBezHran,
          jeVrstva: isVrstva,
          hranyRodica: isVrstva ? hranyRodica(rodic, field) : null,
        }),
      );
      return;
    }
    const current = dielec[field] != null ? String(dielec[field]) : "";
    if (value === current) return;
    if (field === "pocet_ks") {
      await patch({ pocet_ks: parseInt(value, 10) || 1 });
      return;
    }
    if (field === "z_mm" && !isVrstva) {
      // 37.4 a 37.40 je tá istá hrúbka — dialóg by sa otvoril zbytočne
      if (value && parseFloat(value) === parseFloat(current)) return;
      // Skutočná hrúbka lepeného (37,4) patrí k pravidlu 36 (KOLO 188)
      const rule = pravidloPreHrubku(splitRules, value);
      if (rule) {
        setLepeny({ rule, z: value });
        return;
      }
      if (!rule && lepVrstvy.length > 0) {
        // Rodič prechádza na nelepenú hrúbku — vrstvy strácajú zmysel.
        // Súčasti (sklo) sa zmeny Z netýkajú.
        if (
          !window.confirm(
            `Dielec ${dielec.kod} má lepené vrstvy — zrušiť ich a nastaviť ${value} mm?`,
          )
        ) {
          return;
        }
        for (const v of lepVrstvy) {
          await deleteMutation.mutateAsync(v.id);
        }
      }
    }
    await patch({ [field]: value || null });
  }

  async function zmaz(): Promise<void> {
    if (!window.confirm(`Zmazať ${isVrstva ? "vrstvu" : "dielec"} ${dielec.kod}?`)) return;
    try {
      await deleteMutation.mutateAsync(dielec.id);
      toast({ title: "Dielec zmazaný" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  // Vrstva sa ukazuje ako rodič — holá doska a pod ňou rozmer po
  // olepení (KOLO 138); bežný dielec svoje rozmery
  const rozmery = isVrstva ? rozmeryVrstvy(dielec, rodic) : dielec;
  const material = slot?.material ?? dielec.material_direct ?? null;
  // Pomenované kovanie ukáže alias, pravý názov je v tooltipe (KOLO 176)
  const menoMaterialu = material ? menoKovania(material) : null;
  // Doska len kódom (KOLO 195) — „materiály nech majú iba ID", názov je
  // v tooltipe; kovanie ostáva s menom
  const kodDosky = material?.druh === "plosny" ? materialPrimaryId(material) : null;
  const matLabel = slot ? nazovSlotu(slot) : kodDosky ?? menoMaterialu?.hlavne ?? "—";
  const matTitle =
    kodDosky && material
      ? `${kodDosky} · ${material.nazov}`
      : menoMaterialu?.pod
        ? `${matLabel} — ${menoMaterialu.pod}`
        : matLabel;
  // Inline výber slotu — len keď dielec nemá priamy materiál (kusový/dĺžkový
  // s direct materiálom sa vyberá v DielecDetailPanel s vyhľadávaním).
  // Vlastná položka mimo cenníka (KOLO 210) — rozhoduje server; materiál
  // ani slot nemá, cenu nesie riadok
  const jeVlastna = !!dielec.je_vlastna_polozka;
  const slotEditable = !dielec.material_id_direct;
  const jeKovanie = dielec.material_direct?.druh === "kusovy";
  // Sklo (rodič z kalkulačky) — edituje sa cez dialóg Upraviť sklo;
  // nadmiera a operácia preň nemajú zmysel.
  const jeSklo =
    dielec.material_direct?.kategoria === "SKLO" && !dielec.parent_dielec_id;
  // Deti dielca: lepené vrstvy (lep_nc) vs súčasti (sklo: kalenie,
  // vŕtanie, služby) — súčasti majú vlastný popis pod názvom rodiča.
  const lepVrstvy = vrstvy.filter((v) => v.operacia === OPERACIA_LEPENA_VRSTVA);
  const operacieRows = vrstvy.filter(jeOperaciaChild);
  // Lamináty pod doskou (KOLO 234) majú vlastné riadky, nie text súčastí
  const laminatyRows = vrstvy.filter(jeLaminatRiadok);
  const sucasti = vrstvy.filter(
    (v) =>
      v.operacia !== OPERACIA_LEPENA_VRSTVA &&
      !jeOperaciaChild(v) &&
      !jeLaminatRiadok(v),
  );
  // chevron rozbaľuje lep vrstvy, vnorené operácie aj lamináty
  const rozbalitelneDeti =
    lepVrstvy.length + operacieRows.length + laminatyRows.length;
  // Pod názvom: súčasti skla a pridané hrany (KOLO 169) —
  // „+ hrana otvor 1 000 mm", aby bolo vidno, že dielec nesie aj otvor
  const sucastiText =
    sucasti.length > 0
      ? "+ " +
        sucasti
          .map((s) => {
            const nazov = s.nazov_strojovy ?? s.kod;
            return s.pocet_ks != null && s.pocet_ks !== (dielec.pocet_ks ?? 1)
              ? `${nazov} (${s.pocet_ks} ks)`
              : nazov;
          })
          .join(" · ")
      : undefined;
  const podtextDielca =
    [sucastiText, pridaneHranyPodtext(dielec.pridane_hrany)]
      .filter(Boolean)
      .join(" · ") || undefined;
  // Náčrt dielca s hranami (KOLO 180) — rozmer ako v stĺpcoch, vrstva
  // v rozmere rodiča a bez hrán (olepuje sa rodič)
  const nacrt = {
    xMm: rozmery.x_mm != null ? Number(rozmery.x_mm) : null,
    yMm: rozmery.y_mm != null ? Number(rozmery.y_mm) : null,
    smer: dielec.smer_vlakien,
    hrany: isVrstva
      ? {}
      : {
          x1: dielec.hrana_x1_id != null,
          x2: dielec.hrana_x2_id != null,
          y1: dielec.hrana_y1_id != null,
          y2: dielec.hrana_y2_id != null,
        },
    hrubky: dielec.hrany_mm,
    zaoblenie: dielec.masiv
      ? {
          x1: dielec.zaoblenie_x1,
          x2: dielec.zaoblenie_x2,
          y1: dielec.zaoblenie_y1,
          y2: dielec.zaoblenie_y2,
        }
      : undefined,
  };
  const popisHran = popisHranNacrtu(nacrt);
  // Farba materiálu podľa poradia slotu (KOLO 180) — rovnaký materiál,
  // rovnaká farba
  const farbaSlotu = slot
    ? farbaMaterialu(sloty.findIndex((s) => s.id === slot.id))
    : null;

  // Menu riadku (KOLO 181) — akcia ide na celý viacvýber, keď je v ňom
  // riadok; vrstva len sama za seba
  const ciele = isVrstva || !akcie ? [dielec] : akcie.ciele(dielec);
  const viac = ciele.length > 1;
  const vystrihnute = akcie?.schranka.vystrihnute;
  const vystrihnuty =
    vystrihnute?.has(dielec.id) || (isVrstva && rodic != null && vystrihnute?.has(rodic.id));
  const bezHran = jeKovanie || jeVlastna || jeSklo || !!dielec.riadok_z_hrany;
  // Posun v rade ikon menu (KOLO 184) — sivá ikona povie prečo
  function dovodPosunu(smer: -1 | 1): string | null {
    if (isVrstva) return "vrstva ide so sendvičom";
    if (viac) return "posúva sa jeden riadok";
    if (!onPosun) return "tento riadok sa neposúva";
    if (smer === -1 && !mozePosunut?.hore) return "riadok je prvý v zostave";
    if (smer === 1 && !mozePosunut?.dole) return "riadok je posledný v zostave";
    return null;
  }

  const nesieRiadok = (e: React.DragEvent): boolean =>
    tahanie !== undefined && Array.from(e.dataTransfer?.types ?? []).includes(tahanie.mime);
  const spodnaPolovica = (e: React.DragEvent<HTMLElement>): boolean => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY > r.top + r.height / 2;
  };

  return (
    <TableRow
      onClick={onSelect}
      onContextMenu={(e) => {
        if (jeVstup(e.target)) return;
        e.preventDefault();
        // Ako vo Windows: pravé tlačidlo na riadku mimo výberu vyberie
        // ten riadok, vo výbere ho nechá — akcia ide na celý výber
        if (!vybrany && !selected) onSelect(e);
        setMenu({ x: e.clientX, y: e.clientY });
      }}
      onDragOver={
        tahanie
          ? (e) => {
              if (!nesieRiadok(e)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              tahanie.onNad(spodnaPolovica(e));
            }
          : undefined
      }
      onDragLeave={tahanie ? () => tahanie.onOdchod() : undefined}
      onDrop={
        tahanie
          ? (e) => {
              if (!nesieRiadok(e)) return;
              e.preventDefault();
              const id = e.dataTransfer.getData(tahanie.mime);
              if (id) tahanie.onPustenie(id, spodnaPolovica(e));
            }
          : undefined
      }
      className={cn(
        "group/riadok",
        // Dielec je biely (KOLO 100 — reverz KOLA 63): dielcov je najviac,
        // na bielej má text najlepší kontrast a zostava nad ním sa tým
        // číta ako hlavička skupiny. Koľajnica vľavo pod zostavou spája
        // jej položky — druhá polovica hierarchie vedľa odsadenia
        // (`pl-6` / `pl-10` v bunke ID).
        // **Zebra** (KOLO 102): pri 25 dielcoch pod sebou oko stratí
        // riadok na ceste cez ~1500 px šírky. Striedania sa zúčastňujú
        // LEN bežné dielce — zostava, vrstva a operácia majú vlastný
        // odtieň, ktorý by pruh prebil a rozbil hierarchiu.
        "cursor-pointer border-l-4 border-l-transparent bg-riadok-dielec hover:bg-riadok-dielec-hover",
        !isVrstva && parny && "bg-riadok-dielec-parny",
        // Vrstva sendviča (KOLO 180): vlastný odtieň + sivý pruh skupiny,
        // za poslednou čiara — „musí byť vidno, že je o úroveň nižšie"
        isVrstva &&
          "border-l-tab-vnorene bg-riadok-podriadok text-riadok-podriadok-text hover:bg-riadok-podriadok-hover",
        isVrstva && stromPosledny && "!border-b-tab-vnorene",
        // Hromadný výber je tlmenejší než jediný vybraný riadok — inak by
        // pri 10 označených nebolo vidno, ktorý drží detail panel. Od
        // KOLA 180 modrý: červená ostáva chybe hrany „!".
        vybrany && "border-l-vyber-viac-pruh bg-vyber-viac hover:bg-vyber-viac",
        selected && VYBRANY_RIADOK,
        // Čiara, kam riadok pristane — nad alebo pod týmto (KOLO 176);
        // za výberom, aby ho pri ťahaní prebila
        tahanie?.ciel === "nad" && "shadow-[inset_0_2px_0_0_hsl(var(--primary))]",
        tahanie?.ciel === "pod" && "shadow-[inset_0_-2px_0_0_hsl(var(--primary))]",
        // Vystrihnutý, kým sa nevloží (KOLO 181) — ako súbor vo Windows
        vystrihnuty && "opacity-50",
      )}
      data-testid={`dielec-row-${dielec.id}`}
      data-vystrihnuty={vystrihnuty ? "1" : undefined}
      aria-selected={selected}
      data-vybrany={vybrany ? "1" : undefined}
    >
      <VyberCell
        zaskrtnute={vybrany}
        onToggle={onPrepniVyber}
        label={`Vybrať dielec ${dielec.kod}`}
      />
      <TableCell className="p-1">
        {/* Ťahadlo (KOLO 176) — pri prejdení myšou namiesto ikony typu.
            Ťahá sa len za neho: dvojklik na bunku ostáva úprava. */}
        {tahanie ? (
          <span
            draggable
            role="button"
            aria-label={`Posunúť riadok ${dielec.kod}`}
            title="Potiahni a posuň riadok v zostave (Alt+↑ / Alt+↓)"
            onClick={(e) => e.stopPropagation()}
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.setData(tahanie.mime, dielec.id);
              e.dataTransfer.effectAllowed = "move";
            }}
            onDragEnd={() => tahanie.onKoniec()}
            className="hidden cursor-grab justify-center text-muted-foreground hover:text-foreground active:cursor-grabbing group-hover/riadok:flex"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <span className={cn("contents", tahanie && "group-hover/riadok:hidden")}>
        {/* Chevron je od KOLA 136 v strome (stĺpec ID); tu ostáva typ */}
        {lepVrstvy.length > 0 ? (
          <span
            role="img"
            aria-label={`Lepený dielec ${dielec.kod}`}
            title={`Lepený dielec — ${lepVrstvy.length} ${sklonuj(lepVrstvy.length, ["vrstva", "vrstvy", "vrstiev"])} · ${popisHran}`}
            className="flex justify-center"
          >
            <NacrtDielca {...nacrt} sendvic />
          </span>
        ) : jeSklo ? (
          <span
            role="img"
            aria-label={`Sklo ${dielec.kod}`}
            title="Sklo"
            className="flex justify-center"
          >
            <IconSklo className="h-3.5 w-3.5 text-sky-500" />
          </span>
        ) : jeVlastna ? (
          <span
            role="img"
            aria-label={`Vlastná položka ${dielec.kod}`}
            title="Vlastná položka mimo cenníka"
            className="flex justify-center"
          >
            <PackagePlus className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        ) : jeKovanie ? (
          <span
            role="img"
            aria-label={`Kovanie ${dielec.kod}`}
            title="Kovanie"
            className="flex justify-center"
          >
            <IconKusovy className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        ) : (
          <span
            role="img"
            aria-label={`Dielec ${dielec.kod}`}
            title={
              isVrstva
                ? "Lepená vrstva"
                : material?.druh === "dlzkovy"
                  ? "Dielec (dĺžkový materiál)"
                  : `Dielec · ${popisHran}`
            }
            className="flex justify-center"
          >
            {material?.druh === "dlzkovy" ? (
              <IconDlzkovy className="h-3 w-3 text-muted-foreground/70" />
            ) : (
              // Náčrt s hranami (KOLO 180) — vrstva sendviča hrany nemá,
              // tie sú na rodičovi
              <NacrtDielca {...nacrt} />
            )}
          </span>
        )}
        </span>
      </TableCell>
      {/* ID tlmené a menšie — hlavný text riadku je Názov. Vetva stromu
          (KOLO 136): dielec ├─/└─ pod zostavou, vrstva o úroveň hlbšie;
          sendvič a dielec s operáciami sa rozbaľujú priamo na vetve. */}
      <StromBunka
        hlbka={isVrstva ? 2 : 1}
        posledny={stromPosledny}
        rodicPosledny={rodicPosledny}
        rozbalitelne={!isVrstva && rozbalitelneDeti > 0}
        rozbalene={vrstvyExpanded}
        onToggle={onToggleVrstvy}
        toggleLabel={`${vrstvyExpanded ? "Zbaliť" : "Rozbaliť"} vrstvy ${dielec.kod}`}
        toggleTitle={
          lepVrstvy.length > 0
            ? `Lepené vrstvy (${lepVrstvy.length})${operacieRows.length ? ` + operácie (${operacieRows.length})` : ""}`
            : `Operácie (${operacieRows.length})`
        }
        className="font-mono text-[11px] text-muted-foreground"
        title={dielec.kod}
      >
        {dielec.kod}
        {/* Hrana je úzka na hrúbku dosky (KOLO 135, 222) — svieti pri ID */}
        {maChybuHrany(dielec) ? (
          <span
            role="img"
            aria-label={`Hrana nesedí ${dielec.kod}`}
            title={popisStavuHran(dielec)}
            className="ml-0.5 inline-flex align-middle text-destructive"
          >
            <AlertTriangle className="h-3 w-3" />
          </span>
        ) : null}
        {/* Prírez sa nezmestí na tabuľu dosky (KOLO 193) — ručne sa taký
            neuloží, má ho import; Cut Rite ho nepustí */}
        {dielec.prirez_mimo_tabule ? (
          <span
            role="img"
            aria-label={`Prírez väčší ako tabuľa ${dielec.kod}`}
            title={dielec.prirez_mimo_tabule}
            className="ml-0.5 inline-flex align-middle text-destructive"
          >
            <AlertTriangle className="h-3 w-3" />
          </span>
        ) : null}
      </StromBunka>
      <InlineCell
        value={dielec.nazov_strojovy ?? ""}
        subtext={
          isVrstva && (dielec.rozmer_rucne || dielec.material_rucne)
            ? // Výnimka voči rodičovi — rodič ju pri zmene neprepíše (KOLO 131)
              [
                dielec.rozmer_rucne ? "vlastný rozmer" : null,
                dielec.material_rucne ? "vlastný materiál" : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : podtextDielca
        }
        onSave={(v) => patchField("nazov_strojovy", v)}
        mono={false}
        titul={dielec.nazov_strojovy ?? ""}
        className={cn(
          "max-w-[200px] truncate text-sm",
          !isVrstva && "font-medium",
          isVrstva && cn(ODSADENY_NAZOV, "text-[13px]"),
        )}
        autoEdit={autoEditNazov}
        onAutoEditDone={onAutoEditDone}
        label={`Názov dielca ${dielec.kod}`}
        datalist={
          jeKovanie || jeVlastna
            ? undefined
            : nazvyDielcov.map((n) => ({ value: n.nazov }))
        }
      />
      {/* Popis má len zostava (KOLO 139) — user: „popis dielca
          nevyužijem, popis budem dávať len na zostavy". Voľnú bunku
          dielca s deťmi od KOLA 180 zaberá kapsula s počtom: vrstvy aj
          operácie sú predvolene zbalené a dovtedy ich prezradila len
          malá šípka (kolega: „keď je to zavreté, nech je vidno, že tam
          niečo je"). */}
      <TableCell className="px-1">
        {!isVrstva && rozbalitelneDeti > 0 ? (
          <KapsulaDeti
            kod={dielec.kod}
            vrstvy={lepVrstvy.length}
            operacie={operacieRows.length}
            laminaty={laminatyRows.length}
            rozbalene={vrstvyExpanded}
            onToggle={onToggleVrstvy}
          />
        ) : null}
        <StitokUpravyCeny dielec={dielec} />
      </TableCell>
      {jeVlastna ? (
        // Vlastná položka má desatinné množstvo v svojej MJ (KOLO 211) —
        // 12,5 m LED pásu; Ks drží server na 1. MJ je v bunke vedľa, inak
        // sa „12,5 kpl“ v úzkom stĺpci zalomí a riadok narastie
        <InlineCell
          value={mnozstvoVlastnej}
          onSave={(v) => void ulozMnozstvoVlastnej(v)}
          textRight
          label={`Množstvo ${dielec.kod}`}
        />
      ) : (
        <InlineCell
          value={String(dielec.pocet_ks ?? "")}
          onSave={(v) => patchField("pocet_ks", v)}
          textRight
          label={`Počet kusov ${dielec.kod}`}
        />
      )}
      {jeKovanie || jeVlastna ? (
        // Kovanie nemá rozmery, postup ani nadmieru — edituje sa len
        // názov, kusy a samotné kovanie (klik na materiál). Vlastná
        // položka to isté, cenu mení detail (KOLO 210); tu je jej MJ.
        <TableCell colSpan={7} className="pl-1 text-xs text-muted-foreground">
          {jeVlastna ? <span data-testid={`mj-${dielec.kod}`}>{dielec.vlastna_mj ?? "ks"}</span> : null}
        </TableCell>
      ) : (
        <>
          {/* Rozmery v poradí PODĽA SMERU VLÁKIEN — dielec so smerom „Y"
              sa reže otočený, tak sa aj číta prehodene (300×500). Bunka
              edituje vždy to pole, ktorého hodnotu zobrazuje. */}
          {(dielec.smer_vlakien === "Y"
            ? (["y_mm", "x_mm"] as const)
            : (["x_mm", "y_mm"] as const)
          ).map((pole) => (
            <InlineCell
              key={pole}
              value={normalizeNum(zobrazenyRozmer(rozmery, pole))}
              // Holý rozmer: zadaných 500, ďalej ide 502 s hranou (KOLO 131).
              // Pod ním malým len číslo rozmeru po olepení — bez slov
              // „s hranou" (KOLO 133; KOLO 132 omylom zmazalo celý podtext).
              subtext={
                maHolyRozmer(rozmery, pole) &&
                normalizeNum(dielec[pole]) !==
                  normalizeNum(zobrazenyRozmer(rozmery, pole))
                  ? normalizeNum(dielec[pole])
                  : undefined
              }
              titul={
                maHolyRozmer(rozmery, pole)
                  ? `rozmer po olepení ${normalizeNum(dielec[pole])}`
                  : undefined
              }
              onSave={(v) => patchField(pole, v)}
              textRight
              label={`${pole === "x_mm" ? "X" : "Y"} mm ${dielec.kod}`}
            />
          ))}
          {material?.druh === "dlzkovy" ? (
            // Dĺžkový materiál — hrúbku určuje materiál, dielec ju nemá
            <TableCell
              className="text-center text-xs text-muted-foreground"
              title="Hrúbku určuje dĺžkový materiál"
            >
              —
            </TableCell>
          ) : (
            <InlineCell
              value={normalizeNum(dielec.z_mm)}
              subtext={
                lepVrstvy.length > 0
                  ? lepVrstvy.map((v) => normalizeNum(v.z_mm)).join("+")
                  : undefined
              }
              onSave={(v) => patchField("z_mm", v)}
              textRight
              label={`Z mm ${dielec.kod}`}
              moznosti={hrubkyPreDielec(
                hrubky,
                splitRules,
                isVrstva ? null : hrubkaMaterialu(material),
                { lepene: !isVrstva },
              ).map((h) => ({
                value: String(h.mm),
                text: h.lepena
                  ? `lepená${h.vrstvy ? ` ${h.vrstvy.join("+")}` : ""}`
                  : undefined,
              }))}
            />
          )}
          {jeSklo ? (
            // Sklo sa edituje cez dialóg Upraviť sklo — postup, operácie
            // ani nadmiera preň nemajú zmysel, bunky sú read-only.
            <>
              <TableCell
                className="text-center text-xs text-muted-foreground"
                title="Sklo nemá výrobný postup"
              >
                —
              </TableCell>
              <TableCell
                className="text-center text-xs text-muted-foreground"
                title="Sklo sa na CNC nereže"
              >
                —
              </TableCell>
              <TableCell
                className="text-center text-xs text-muted-foreground"
                title="Sklo nemá nadmieru"
              >
                —
              </TableCell>
            </>
          ) : (
            <>
              {/* Postup sa **vyberá**, nepíše (KOLO 108). Voľný text tu
                  dovtedy padal do `nazov_volny`, teda do záložného názvu
                  dielca — objavil sa v rozkrese aj v podklade pre pílu.
                  Vlastný text má odteraz vlastný stĺpec vedľa. */}
              <TableCell className="p-1" onClick={(e) => e.stopPropagation()}>
                {isVrstva ? (
                  /* Vrstva sendviča má postup **pevne daný** — je to
                     štrukturálna značka, podľa ktorej appka vrstvu pozná
                     (rodič vďaka nej neplatí dosku a nereže sa na tabuli).
                     Prepnutím by vrstva ticho prestala byť vrstvou
                     a doska by sa započítala dvakrát, preto sa tu
                     nevyberá. Mení sa rozpustením sendviča. */
                  <span
                    className="block max-w-[110px] truncate py-1 text-xs text-muted-foreground"
                    title="Vrstva sendviča — postup je daný lepením. Zmeníš ho rozpustením lepeného dielca."
                    aria-label={`Výrobný postup vrstvy ${dielec.kod}`}
                  >
                    {postup ? postupPopis(postup) : (dielec.operacia ?? "—")}
                  </span>
                ) : (
                  <select
                    value={dielec.operacia ?? ""}
                    onChange={(e) => void patch({ operacia: e.target.value || null })}
                    disabled={!!zamok}
                    aria-label={`Výrobný postup dielca ${dielec.kod}`}
                    title={
                      lepVrstvy.length > 0
                        ? "Lepený dielec sa vždy lepí — ponúkajú sa len postupy s lepením (KOLO 138)"
                        : postupTitul(postup)
                    }
                    className="w-full max-w-[110px] truncate rounded border border-transparent bg-transparent py-1 text-xs text-muted-foreground hover:border-border focus:border-border focus:outline-none"
                  >
                    {/* Sendvič sa vždy lepí (KOLO 138) — bez „—" a bez
                        olep_nc; backend iný postup odmietne */}
                    {lepVrstvy.length > 0 ? null : <option value="">—</option>}
                    {(lepVrstvy.length > 0
                      ? operacie.filter((o) => jeLepiaciPostup(o.kod))
                      : operacie
                    ).map((o) => (
                      <option key={o.kod} value={o.kod}>
                        {postupPopis(o)}
                      </option>
                    ))}
                    {/* Dielec môže niesť kód, ktorý sa už neponúka
                        (`hw`, `nc_lep_sucho`, alebo postup, ktorý user
                        odčísloval). Bez tejto option by `<select>` nemal
                        čo vybrať a vykreslil by „—" — teda klamstvo
                        o tom, čo je v DB. */}
                    {dielec.operacia && !postup ? (
                      <option value={dielec.operacia}>
                        {dielec.operacia} (mimo zoznamu)
                      </option>
                    ) : null}
                  </select>
                )}
              </TableCell>
              {/* Operácie: strojová časť tlmene (appka si ju počíta sama),
                  vlastný text usera plnou farbou. Dvojklik edituje **len**
                  ten text — `rez`/`združ 2×` nie je čo prepisovať. */}
              <OperacieCell
                strojova={strojovaOperacia(cncRiadok)}
                popis={dielec.operacie_popis ?? ""}
                onSave={(v) => void patch({ operacie_popis: v || null })}
                label={`Operácie dielca ${dielec.kod}`}
              />
              {/* Nadmiera je od KOLA 107 **priamo v tabuľke ako dve čísla**,
                  nie fajka. Odkedy je znamienková (KOLO 104), je to údaj,
                  ktorý sa líši dielec od dielca podľa hrán — fajka „zapnuté"
                  o ňom nepovedala nič a hodnotu bolo treba hľadať v detaile.
                  Zap/vyp zostáva v detaile (Spracovanie → Nadmiery), lebo je
                  to výnimka, nie každodenná úprava. */}
              <TableCell className="px-1 text-center font-mono text-xs">
                <span className="inline-flex items-center gap-0.5">
                  {/* Ukazuje sa nadmiera tak, ako ju číta dielňa — pri
                      postupe najprv na CNC k holej doske (+5), uloží sa
                      k rozmeru s hranou (KOLO 133, `lib/nadmiera.ts`). */}
                  <NadmieraInput
                    value={zobrazenaNadmiera(dielec, "x")}
                    label={`Nadmiera X dielca ${dielec.kod}`}
                    vypnuta={!dielec.nadmiera_aktivna}
                    auto={dielec.nadmiera_auto ?? true}
                    onSave={(v) =>
                      void patch({
                        nadmiera_x_mm: nadmieraNaUlozenie(dielec, "x", v),
                        nadmiera_auto: false,
                      })
                    }
                  />
                  <span className="text-muted-foreground">/</span>
                  <NadmieraInput
                    value={zobrazenaNadmiera(dielec, "y")}
                    label={`Nadmiera Y dielca ${dielec.kod}`}
                    vypnuta={!dielec.nadmiera_aktivna}
                    auto={dielec.nadmiera_auto ?? true}
                    onSave={(v) =>
                      void patch({
                        nadmiera_y_mm: nadmieraNaUlozenie(dielec, "y", v),
                        nadmiera_auto: false,
                      })
                    }
                  />
                </span>
              </TableCell>
              {/* Konečný (hrubý) rozmer po všetkých nadmierach — poradie
                  podľa smeru vlákien, rovnako ako stĺpce Dĺžka/Šírka.
                  Pod ním počet prírezov + názov operácie, aby bolo vidno,
                  čo sa s dielcom na stroji robí (KOLO 45). */}
              <TableCell
                className={cn(
                  // `max-w` + `truncate`, lebo `table-layout: auto` z `w-[100px]`
                  // v hlavičke robí len preferenciu (KOLO 99). Široký je až
                  // druhý riadok `2× nc_zdruz_rez…`; plné znenie je v `title`
                  // a v detaile dielca (karta PRÍREZ).
                  //
                  // KOLO 107: `px-1` namiesto `p-3` a 100 px namiesto 90 —
                  // odkedy sa hrana odčítava, prírez má desatinu
                  // (`1851×500,5` = 10 znakov) a do 90 px s 24 px paddingu sa
                  // nezmestil, takže sa orezal na `1851×500…`.
                  //
                  // KOLO 132: 112 px — prírez z holého rozmeru má stotiny
                  // (`500.9×300.95` = 12 znakov) a do 92 px sa orezal.
                  "max-w-[112px] truncate px-1 text-right font-mono text-xs",
                  cncVarovanie
                    ? "cursor-pointer text-cnc-varovanie hover:underline"
                    : "text-muted-foreground",
                )}
                onClick={
                  cncVarovanie
                    ? (e) => {
                        e.stopPropagation();
                        setPrirezOpen(true);
                      }
                    : undefined
                }
                title={prirezTitul(
                  cncRiadok,
                  cncVarovanie,
                  dielec.pocet_ks ?? 1,
                )}
              >
                {/* Ikona sedí pri príreze, nie pri nadmiere (KOLO 107):
                    hovorí „dielec sa nezmestí na CNC, prírez je zväčšený",
                    teda o tomto stĺpci. V stĺpci Nadmiera navyše brala
                    miesto dvom číslam. */}
                {cncVarovanie ? (
                  <AlertTriangle
                    className="mr-0.5 inline h-3.5 w-3.5 align-[-2px] text-cnc-varovanie"
                    aria-label="CNC prírez"
                  />
                ) : null}
                {cncRiadok != null
                  ? (() => {
                      const [p1, p2] = zobrazRozmer(
                        cncRiadok.hrubyX,
                        cncRiadok.hrubyY,
                        dielec.smer_vlakien,
                      );
                      return `${normalizeNum(p1)}×${normalizeNum(p2)}`;
                    })()
                  : "—"}
                {/* Druhý riadok `2× nc_zdruz_rez…` odišiel do stĺpca
                    Operácie (KOLO 108) — presné označenie pre dielňu
                    ostáva v `title` a v karte PRÍREZ. Stĺpec Prírez je
                    odteraz jedno číslo. */}
              </TableCell>
            </>
          )}
        </>
      )}
      {jeVlastna ? (
        <TableCell
          className="max-w-[160px] truncate text-xs"
          title={`${stitokVlastnej(dielec.vlastna_dodavatel)} — nákup ${formatEur(
            dielec.vlastna_nakup,
          )} / ${dielec.vlastna_mj ?? "ks"}, réžia ${normalizeNum(dielec.vlastna_rezia_pct)} %${
            Number(dielec.vlastna_praca ?? 0) > 0
              ? `, práca ${formatEur(dielec.vlastna_praca)} / ${dielec.vlastna_mj ?? "ks"}`
              : ""
          }`}
        >
          <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5">
            <PackagePlus className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate">{stitokVlastnej(dielec.vlastna_dodavatel)}</span>
          </span>
        </TableCell>
      ) : slotEditable ? (
        <TableCell
          className="text-xs p-1"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Štítok materiálu (KOLO 180): podklad a prúžok vľavo vo farbe
              slotu — čo ide z jednej dosky, vidno bez čítania kódov.
              Prúžok je tieň, nie samostatný štvorček: stĺpec je úzky. */}
          <select
            data-farba-materialu={farbaSlotu?.stvorcek ?? ""}
            value={dielec.material_slot_id ?? ""}
            disabled={!!zamok}
            onChange={(e) => {
              const slotId = e.target.value || null;
              const varId = sloty[0]?.varianta_id;
              if (varId) setLastSlot(varId, slotId);
              void patch({ material_slot_id: slotId });
            }}
            aria-label={`Materiál dielca ${dielec.kod}`}
            title={slot ? popisSlotu(slot) : undefined}
            style={
              farbaSlotu
                ? {
                    backgroundColor: farbaSlotu.podklad,
                    boxShadow: `inset 4px 0 0 ${farbaSlotu.stvorcek}`,
                  }
                : undefined
            }
            className="w-full max-w-[160px] truncate rounded border border-transparent bg-transparent py-1 pl-1.5 text-xs hover:border-border focus:border-border focus:outline-none"
          >
            <option value="">—</option>
            {slotyPreDielec(sloty, slot).map((s) => (
              <option key={s.id} value={s.id} title={popisSlotu(s)}>
                {nazovSlotu(s)}
              </option>
            ))}
          </select>
        </TableCell>
      ) : jeKovanie ? (
        <TableCell
          className="text-xs p-1"
          onClick={(e) => e.stopPropagation()}
          title={matTitle}
        >
          <button
            type="button"
            onClick={() => setZmenaKovania(true)}
            disabled={!!zamok}
            aria-label={`Zmeniť kovanie ${dielec.kod}`}
            title={zamok ? matTitle : "Klik = zmeniť kovanie"}
            className="flex w-full max-w-[160px] items-center gap-1 rounded border border-transparent px-1 py-1 text-left hover:border-border"
          >
            <IconKusovy className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate">{matLabel}</span>
          </button>
        </TableCell>
      ) : (
        <TableCell className="max-w-[160px] truncate text-xs" title={matTitle}>
          <span className="inline-flex items-center gap-1">
            {material?.druh === "dlzkovy" ? (
              <IconDlzkovy className="h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <IconPlosny className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate">{matLabel}</span>
          </span>
        </TableCell>
      )}
      <TableCell className="px-1 text-right">
        <TlacidloMenu kod={dielec.kod} onOtvor={setMenu} />
        {menu && akcie ? (
          <MenuRiadku
            poloha={menu}
            onClose={() => setMenu(null)}
            hlavicka={
              viac
                ? `${popisPoctu(ciele.length)} vo výbere`
                : `${dielec.kod ?? ""} · ${dielec.nazov_strojovy ?? dielec.nazov_volny ?? ""}`
            }
            zbalit={
              isVrstva
                ? {
                    rozbalene: true,
                    label: `Zbaliť vrstvy a operácie ${rodic?.kod ?? ""}`.trim(),
                    onClick: () => onToggleVrstvy?.(),
                  }
                : rozbalitelneDeti > 0
                  ? {
                      rozbalene: vrstvyExpanded,
                      label: `${vrstvyExpanded ? "Zbaliť" : "Rozbaliť"} vrstvy a operácie`,
                      onClick: () => onToggleVrstvy?.(),
                    }
                  : {
                      rozbalene: true,
                      label: "Zbaliť zostavu",
                      onClick: () => onZbalZostavu?.(),
                      dovod: onZbalZostavu ? null : "tu sa nedá zbaliť",
                    }
            }
            kopirovat={{
              label: viac ? `Kopírovať ${popisPoctu(ciele.length)}` : "Kopírovať",
              onClick: () => akcie.schranka.kopiruj(ciele),
              dovod: isVrstva ? "vrstva ide so sendvičom — kopíruj celý dielec" : null,
            }}
            vlozit={{
              label: isVrstva ? `Vložiť za ${rodic?.kod ?? "dielec"}` : "Vložiť pod tento riadok",
              onClick: () =>
                void akcie.schranka.vloz(
                  dielec.zostava_id,
                  isVrstva ? (rodic?.id ?? null) : dielec.id,
                ),
              dovod: akcie.schranka.moznost.ok ? null : akcie.schranka.moznost.dovod,
            }}
            vystrihnut={{
              label: viac ? `Vystrihnúť ${popisPoctu(ciele.length)}` : "Vystrihnúť",
              onClick: () => akcie.schranka.vystrihni(ciele),
              dovod: isVrstva ? "vrstva ide so sendvičom — vystrihni celý dielec" : null,
            }}
            hore={{
              label: "Posunúť hore",
              onClick: () => onPosun?.(-1),
              dovod: dovodPosunu(-1),
            }}
            dole={{
              label: "Posunúť dole",
              onClick: () => onPosun?.(1),
              dovod: dovodPosunu(1),
            }}
          >
            <PolozkyPridatAZbalit zostavaId={dielec.zostava_id} bezZbalenia />
            {!isVrstva && !viac && !jeKovanie && !jeSklo ? (
              <PolozkaMenu ikona={IconOperacia} onSelect={() => setOpDialogOpen(true)}>
                Pridať operáciu
              </PolozkaMenu>
            ) : null}
            {/* Laminovať (KOLO 234) — aj viac vybraných dosiek naraz */}
            {!isVrstva && !jeKovanie && !jeSklo && akcie?.laminuj ? (
              <PolozkaMenu ikona={Layers} onSelect={() => akcie.laminuj?.(ciele)}>
                Laminovať…
              </PolozkaMenu>
            ) : null}
            {/* Náhľad prírezu — len keď sa dielec predrezáva/združuje/rotuje */}
            {!viac && cncVarovanie ? (
              <PolozkaMenu ikona={Crop} citanie onSelect={() => setPrirezOpen(true)}>
                Pozrieť prírez
              </PolozkaMenu>
            ) : null}
            {!isVrstva && !viac ? (
              <PolozkaMenu ikona={CopyPlus} onSelect={onDuplicate}>
                Duplikovať
              </PolozkaMenu>
            ) : null}
            {!isVrstva && !bezHran ? (
              <>
                {!viac ? (
                  <PolozkaMenu
                    ikona={RectangleHorizontal}
                    citanie
                    onSelect={() => {
                      nastavSchrankuHran(schrankaZDielca(dielec));
                      toast({ title: `Hrany z ${dielec.kod ?? "dielca"} skopírované` });
                    }}
                  >
                    Kopírovať hrany
                  </PolozkaMenu>
                ) : null}
                <PolozkaMenu
                  ikona={RectangleHorizontal}
                  disabled={!schrankaHran}
                  onSelect={() => void akcie.vlozHrany(ciele)}
                >
                  {schrankaHran
                    ? `Vložiť hrany z ${schrankaHran.kod ?? "dielca"}`
                    : "Vložiť hrany"}
                </PolozkaMenu>
              </>
            ) : null}
            <PolozkaZbalitVsetko />
            <OddelovacMenu />
            <PolozkaMenu
              ikona={Trash2}
              nebezpecna
              skratka={viac || isVrstva ? undefined : "Del"}
              onSelect={() => void (viac ? akcie.zmazRiadky(ciele) : zmaz())}
            >
              {viac
                ? `Zmazať ${popisPoctu(ciele.length)}`
                : isVrstva
                  ? "Zmazať vrstvu"
                  : "Zmazať dielec"}
            </PolozkaMenu>
          </MenuRiadku>
        ) : null}
      </TableCell>
      {prirezOpen && cncRiadok ? (
        <PrirezDialog
          open
          onOpenChange={(o) => setPrirezOpen(o)}
          stav={cncRiadok}
          cistaX={Number(dielec.x_mm)}
          cistaY={Number(dielec.y_mm)}
          pocetKs={dielec.pocet_ks ?? 1}
          kod={dielec.kod}
          smerVlakien={dielec.smer_vlakien}
        />
      ) : null}
      {lepeny ? (
        <LepenyConvertDialog
          open
          zakazkaId={zakazkaId}
          dielec={dielec}
          rule={lepeny.rule}
          zvolenaZ={lepeny.z}
          vrstvy={lepVrstvy}
          onClose={() => setLepeny(null)}
        />
      ) : null}
      {opDialogOpen ? (
        <PridatOperaciuDialog
          zakazkaId={zakazkaId}
          dielec={dielec}
          open
          onOpenChange={(o) => {
            setOpDialogOpen(o);
            if (!o) onExpandVrstvy?.();
          }}
        />
      ) : null}
      {zmenaKovania ? (
        <KovanieChangeDialog
          open
          zakazkaId={zakazkaId}
          dielec={dielec}
          onOpenChange={(o) => {
            if (!o) setZmenaKovania(false);
          }}
        />
      ) : null}
    </TableRow>
  );
}

/**
 * Editovateľná bunka — DVOJKLIK/Enter/F2 = edit mode (jednoduchý klik
 * bublá na riadok = select), Enter/blur = save, Esc = cancel,
 * Tab = save + skok na ďalšiu bunku riadku.
 */
/** `title` bunky výrobného postupu — popis z Nastavení, inak návod. */
function postupTitul(postup: Operation | null): string {
  if (postup == null) {
    return "Cez akú dielňu dielec ide — vyber postup zo zoznamu (Nastavenia → Výroba a CNC → Výrobné postupy)";
  }
  const popis = postup.label ?? postup.popis;
  return popis ? `${postupPopis(postup)} — ${popis}` : postupPopis(postup);
}

/**
 * Čo s dielcom spraví stroj — `rez 302 × 51,5`, `združ 2× rez 150 × 40`,
 * `rot rez …`.
 *
 * Je to **to isté, čo počíta cena aj porez** (`cncStav` je zrkadlo
 * backendu), len po slovensky; zrkadlo `export_dielcov.strojova_operacia`
 * (podklad pre pílu, Cut Rite). Do KOLA 108 to viselo ako druhý riadok
 * v stĺpci Prírez; presné označenie pre dielňu (`nc_zdruz_rez_2x818x36`)
 * ostáva v `title` prírezu a v karte PRÍREZ.
 *
 * Od KOLA 195 s rozmerom, na ktorý sa zväčšený prírez dorezáva — user:
 * *„píš, že rez, a na ten rozmer, čo ho máš odrezať"*.
 */
function strojovaOperacia(stav: CncStav | null): string {
  if (stav == null || !stav.cncDielec || !stav.oznacenie) return "";
  const druh =
    stav.kusovVPrireze > 1
      ? `združ ${stav.kusovVPrireze}× rez`
      : stav.dovod === "rotacia"
        ? "rot rez"
        : "rez";
  return `${druh} ${mmText(stav.rez.dlha)} × ${mmText(stav.rez.kratka)}`;
}

/**
 * Stĺpec Operácie: strojová časť tlmene, vlastný text usera plnou farbou.
 *
 * Dvojklik edituje **len ten text** — `združ 2×` si appka počíta sama
 * a prepisovať ho nie je čo. Ovládanie je zámerne rovnaké ako
 * `InlineCell` (dvojklik/Enter/F2, Enter uloží, Escape zahodí); vlastný
 * komponent je preto, že `InlineCell` renderuje celý `<TableCell>` a tu
 * majú v jednej bunke byť dve veci s rôznym pôvodom (rovnaký dôvod ako
 * `NadmieraInput` v KOLE 107).
 */
function OperacieCell({
  strojova,
  popis,
  onSave,
  label,
}: {
  strojova: string;
  popis: string;
  onSave: (v: string) => void;
  label: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(popis);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const zamok = useZamokTabulky();

  useEffect(() => {
    if (!editing) setVal(popis);
  }, [popis, editing]);

  // Tab z predchádzajúcej bunky sem doskáče cez `data-inline-cell`
  // a otvorí ju **bez myšacej udalosti** — syntetický dblclick otváral
  // prehliadaču aj natívny datalist popup (KOLO 62).
  useEffect(() => {
    const el = cellRef.current;
    if (!el || zamok) return;
    const otvor = (): void => setEditing(true);
    el.addEventListener(INLINE_EDIT_EVENT, otvor);
    return () => el.removeEventListener(INLINE_EDIT_EVENT, otvor);
  });

  function uloz(): void {
    setEditing(false);
    if (val.trim() !== popis) onSave(val.trim());
  }

  if (editing) {
    return (
      <TableCell
        ref={cellRef}
        data-inline-cell
        className="p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={uloz}
          onKeyDown={(e) => {
            if (e.key === "Enter") uloz();
            if (e.key === "Escape") {
              setVal(popis);
              setEditing(false);
            }
          }}
          aria-label={label}
          maxLength={255}
          className="w-full rounded border border-border bg-background px-1 py-0.5 text-xs focus:outline-none"
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      ref={cellRef}
      data-inline-cell
      className="max-w-[190px] truncate px-1 text-xs"
      onDoubleClick={(e) => {
        if (zamok) return;
        e.stopPropagation();
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (zamok) return;
        if (e.key === "Enter" || e.key === "F2") {
          e.stopPropagation();
          setEditing(true);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={label}
      title={
        [strojova, popis].filter(Boolean).join(" · ") ||
        "Dvojklik — vlastný popis operácie"
      }
    >
      {strojova ? (
        <span className="text-muted-foreground">{strojova}</span>
      ) : null}
      {strojova && popis ? (
        <span className="text-muted-foreground"> · </span>
      ) : null}
      {popis ? <span>{popis}</span> : null}
    </TableCell>
  );
}

/** Prechod Tab-om na ďalšiu inline bunku (bez myšacej udalosti). */
const INLINE_EDIT_EVENT = "tandem:inline-edit";

/**
 * Čo sa so stĺpcom Prírez povie v `title`.
 *
 * Vytknuté z JSX (KOLO 107), lebo odkedy ikona varovania sedí pri príreze,
 * musí dôvod zväčšenia povedať tento `title` — a štyri vetvy v zanorenom
 * ternárnom výraze uprostred tabuľky sa už nedali čítať.
 */
function prirezTitul(
  stav: CncStav | null,
  varovanie: boolean,
  pocetKs: number,
): string {
  if (stav == null) return "Konečný rozmer po nadmierach (prírez na pílu)";
  const rozmer = `${normalizeNum(stav.hrubyX)}×${normalizeNum(stav.hrubyY)} mm`;
  const klik = " — klik zobrazí nákres";
  if (varovanie) {
    if (stav.dovod === "rotacia") {
      return `Rotácia kvôli čelám — prírez ${rozmer}${klik}`;
    }
    if (stav.dovod === "zdruzenie") {
      return `Združenie — ${stav.kusovVPrireze} ks v jednom príreze ${rozmer}${klik}`;
    }
    return `Dielec sa nezmestí na CNC — prírez zväčšený na ${rozmer}${klik}`;
  }
  if (!stav.oznacenie) return "Konečný rozmer po nadmierach (prírez na pílu)";
  const kusy =
    stav.kusovVPrireze > 1
      ? ` (${pocetKs} ks → ${stav.pocetPrirezov} prírezov)`
      : "";
  return `${stav.oznacenie} — ${stav.popisOperacie}${kusy}${klik}`;
}

/**
 * Znamienková nadmiera do `title` a na obrazovku: `+5`, `-0,5`, `0`.
 *
 * Znamienko je pri nule zbytočné a inak podstatné — `-0,5` je odpočet
 * hrany na predfrez, `+5` je prídavok operácie (KOLO 104). Bez neho by
 * `0,5` a `-0,5` vyzerali rovnako dôležito a líšili by sa čiarkou.
 */
function nadmieraText(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return "0";
  return (n > 0 ? "+" : "-") + normalizeNum(String(Math.abs(n)));
}

/**
 * Jedno číslo nadmiery priamo v tabuľke — pozri, prepíš, hotovo.
 *
 * **Prečo nie `InlineCell`** (KOLO 107): ten renderuje celý `<TableCell>`,
 * ale do stĺpca Nadmiera patria **dve** čísla vedľa seba. Ovládanie je
 * zámerne to isté — dvojklik alebo Enter/F2 otvorí vstup, Enter uloží,
 * Escape zahodí — inak by sa tabuľka správala na jednom mieste inak.
 *
 * **Ručný prepis vypne auto** (`nadmiera_auto: false`, rieši to volajúci),
 * rovnako ako v detaile dielca: appka do čísla už nesiahne. Auto hodnota
 * je preto tlmená a vlastná má plnú farbu — inak sa nedá rozoznať číslo,
 * ktoré sa pri zmene hrany prepočíta, od toho, ktoré zostane.
 */
function NadmieraInput({
  value,
  label,
  auto,
  vypnuta,
  onSave,
}: {
  value: string | number | null | undefined;
  label: string;
  /** Drží hodnotu appka (prepočíta ju z operácie a hrán)? */
  auto: boolean;
  /** `nadmiera_aktivna=false` — čísla sa do prírezu nezapočítajú. */
  vypnuta: boolean;
  onSave: (v: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const zobrazene = nadmieraText(value);
  const ulozene = parseDecimal(normalizeNum(String(value ?? "0"))) || "0";
  const zamok = useZamokTabulky();

  function start(e?: React.SyntheticEvent): void {
    if (zamok) return;
    e?.stopPropagation();
    setDraft(ulozene);
    setEditing(true);
  }

  function commit(): void {
    setEditing(false);
    const out = parseDecimal(draft) || "0";
    if (out !== ulozene) onSave(out);
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        aria-label={label}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
          }
        }}
        inputMode="decimal"
        className="h-6 w-[42px] px-0.5 text-center font-mono text-xs"
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      onDoubleClick={start}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "F2") {
          e.preventDefault();
          start(e);
        }
      }}
      title={
        (vypnuta
          ? "Nadmiera je vypnutá (detail dielca → Spracovanie) — do prírezu nevstupuje"
          : auto
            ? "Drží ju appka z operácie a hrán"
            : "Prepísaná ručne — appka ju už neprepočíta") +
        " · dvojklik = upraviť"
      }
      className={cn(
        "min-w-[28px] cursor-pointer rounded px-0.5 hover:bg-background",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        vypnuta && "line-through opacity-50",
        !vypnuta && auto && "text-muted-foreground",
      )}
    >
      {zobrazene}
    </span>
  );
}

function InlineCell({
  value,
  display,
  subtext,
  onSave,
  textRight = false,
  mono = true,
  formatter,
  className,
  label,
  titul,
  autoEdit = false,
  onAutoEditDone,
  datalist,
  moznosti,
}: {
  value: string;
  /** Zobrazená hodnota, keď sa líši od editovanej (napr. operácia). */
  display?: string;
  /** Drobný tlmený text pod hodnotou (napr. zloženie lepenia 18+18). */
  subtext?: string;
  onSave: (v: string) => void;
  textRight?: boolean;
  mono?: boolean;
  formatter?: (v: string) => string;
  className?: string;
  label?: string;
  /**
   * Plné znenie do `title` — pri orezanom stĺpci (Názov) je to jediné
   * miesto, kde sa dá prečítať celý text. Skladá sa s hintom o dvojkliku,
   * aby sa `title` nerovnal presne hodnote (testy hľadajú bunky podľa
   * `getByTitle` a dve zhodné by boli nejednoznačné).
   */
  titul?: string;
  autoEdit?: boolean;
  onAutoEditDone?: () => void;
  /** Ponúkané hodnoty (datalist) — napr. operácie z Nastavení. */
  datalist?: { value: string; text?: string }[];
  /**
   * Celý zoznam na výber pod poľom (KOLO 162, stĺpec Z). Natívny datalist
   * filtruje podľa textu v poli, takže pri „18" ukázal len 18 — user:
   * *„teraz mi ukazuješ iba hrúbku, ktorú tam má zadanú"*. Písať sa dá ďalej.
   */
  moznosti?: { value: string; text?: string }[];
}): JSX.Element {
  const listId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [aktivna, setAktivna] = useState(-1);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Zámok tabuľky (KOLO 231) — bunka len zobrazí, dvojklik ani Tab ju
  // neotvoria
  const zamok = useZamokTabulky();

  useEffect(() => {
    if (zamok) return;
    if (autoEdit && !editing) {
      setDraft(value);
      setEditing(true);
      onAutoEditDone?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit]);

  function startEdit(e?: React.SyntheticEvent): void {
    if (zamok) return;
    e?.stopPropagation();
    setDraft(value);
    setAktivna(-1);
    setEditing(true);
  }

  // Bez pole závislostí zámerne — handler musí vidieť aktuálne `value`.
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;
    const otvor = (): void => startEdit();
    el.addEventListener(INLINE_EDIT_EVENT, otvor);
    return () => el.removeEventListener(INLINE_EDIT_EVENT, otvor);
  });

  function commit(hodnota: string = draft): void {
    setEditing(false);
    // Číselné (mono) bunky: akceptuj desatinnú čiarku aj bodku.
    // Textové bunky (názov, operácia) nechaj bez zmeny.
    const out = mono ? parseDecimal(hodnota) : hodnota;
    if (out !== value) onSave(out);
  }

  /** Hodnota zvýraznená šípkami v zozname možností, inak písaná. */
  function vybrana(): string {
    return moznosti?.[aktivna]?.value ?? draft;
  }

  function cancel(): void {
    setEditing(false);
    setDraft(value);
  }

  /** Tab → ďalšia bunka riadku, Shift+Tab → predchádzajúca (KOLO 159). */
  function focusNextCell(smer: 1 | -1 = 1): void {
    const row = cellRef.current?.closest("tr");
    if (!row) return;
    const cells = Array.from(
      row.querySelectorAll<HTMLTableCellElement>("[data-inline-cell]"),
    );
    const idx = cells.indexOf(cellRef.current!);
    const next = cells[idx + smer];
    if (next) {
      // Vlastná udalosť, NIE syntetický dblclick: myšacia udalosť prehliadaču
      // signalizuje interakciu ukazovateľom, takže vstupu s `list` otvoril aj
      // natívny datalist popup. Ten sa pri Tab-e na Operáciu kreslil smerom
      // hore cez pol tabuľky a vyzeral ako rozbitý obdĺžnik.
      requestAnimationFrame(() =>
        next.dispatchEvent(new CustomEvent(INLINE_EDIT_EVENT)),
      );
    }
  }

  const shown = display !== undefined ? display : value;

  return (
    <TableCell
      ref={cellRef}
      data-inline-cell
      tabIndex={editing ? -1 : 0}
      onDoubleClick={editing ? undefined : startEdit}
      onKeyDown={
        editing
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === "F2") {
                e.preventDefault();
                startEdit(e);
              }
            }
      }
      aria-label={label}
      title={
        editing
          ? undefined
          : zamok
            ? (titul ?? undefined)
            : titul
              ? `${titul} · dvojklik = upraviť`
              : "Dvojklik = upraviť"
      }
      className={cn(
        textRight && "text-right",
        mono ? "font-mono" : "font-sans",
        "text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        // v edit móde zmenšiť padding, aby sa input nezrezával v úzkych stĺpcoch
        editing && "p-1",
        className,
      )}
    >
      {editing ? (
        <>
          <Input
            autoFocus
            value={draft}
            ref={inputRef}
            onChange={(e) => {
              setDraft(e.target.value);
              setAktivna(-1);
            }}
            // Hodnota označená — písanie ju prepíše (KOLO 159, user: „keď
            // dám Tab a zadávam rozmery, chcem, aby som vedel prepísať tú
            // hodnotu"; do vtedy sa písalo za koniec)
            onFocus={(e) => e.currentTarget.select()}
            onBlur={() => commit()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (moznosti && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
                e.preventDefault();
                const n = moznosti.length;
                if (n === 0) return;
                setAktivna((i) =>
                  e.key === "ArrowDown" ? (i + 1) % n : (i <= 0 ? n - 1 : i - 1),
                );
              } else if (e.key === "Enter") {
                e.preventDefault();
                commit(vybrana());
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              } else if (e.key === "Tab") {
                e.preventDefault();
                commit(vybrana());
                focusNextCell(e.shiftKey ? -1 : 1);
              }
            }}
            className={cn(
              // Farba písma vlastná — v páse zostavy by pole zdedilo svetlý
              // text pásu a na bielom poli by písmo zaniklo (KOLO 183)
              "h-7 w-full min-w-[3.5rem] bg-background px-1 text-sm text-foreground",
              textRight && "text-right",
              mono && "font-mono",
            )}
            inputMode={mono ? "decimal" : "text"}
            list={datalist ? listId : undefined}
            role={moznosti ? "combobox" : undefined}
            aria-expanded={moznosti ? moznosti.length > 0 : undefined}
            aria-controls={moznosti ? `${listId}-moznosti` : undefined}
            aria-autocomplete={moznosti ? "list" : undefined}
          />
          {datalist ? (
            <datalist id={listId}>
              {datalist.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.text}
                </option>
              ))}
            </datalist>
          ) : null}
          {moznosti && moznosti.length > 0 ? (
            <ZoznamMoznosti
              id={`${listId}-moznosti`}
              kotva={inputRef.current}
              moznosti={moznosti}
              aktivna={aktivna}
              aktualna={draft}
              label={label}
              onVyber={(v) => commit(v)}
            />
          ) : null}
        </>
      ) : (
        <>
          <span>{shown ? (formatter ? formatter(shown) : shown) : "—"}</span>
          {subtext ? (
            <span className="block text-[10px] leading-tight text-muted-foreground">
              {subtext}
            </span>
          ) : null}
        </>
      )}
    </TableCell>
  );
}

/**
 * Zoznam možností pod bunkou (KOLO 162) — v portáli s `fixed` polohou, aby
 * ho neorezal posuvný kontajner tabuľky. `onMouseDown` + `preventDefault`,
 * inak by klik najprv zavrel bunku cez `onBlur` vstupu.
 */
function ZoznamMoznosti({
  id,
  kotva,
  moznosti,
  aktivna,
  aktualna,
  label,
  onVyber,
}: {
  id: string;
  kotva: HTMLElement | null;
  moznosti: { value: string; text?: string }[];
  aktivna: number;
  aktualna: string;
  label?: string;
  onVyber: (v: string) => void;
}): JSX.Element {
  const r = kotva?.getBoundingClientRect();
  return createPortal(
    <ul
      id={id}
      role="listbox"
      aria-label={label ? `Možnosti ${label}` : "Možnosti"}
      className="fixed z-[60] max-h-60 min-w-[7rem] overflow-y-auto rounded-md border bg-popover p-1 text-sm text-popover-foreground shadow-md"
      style={{
        top: (r?.bottom ?? 0) + 2,
        left: r?.left ?? 0,
        minWidth: r?.width,
      }}
    >
      {moznosti.map((o, i) => (
        <li
          key={o.value}
          role="option"
          aria-selected={i === aktivna || (aktivna < 0 && o.value === aktualna)}
          onMouseDown={(e) => {
            e.preventDefault();
            onVyber(o.value);
          }}
          className={cn(
            "flex cursor-pointer items-baseline justify-between gap-3 rounded-sm px-2 py-1 font-mono",
            i === aktivna ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
          )}
        >
          <span>{o.value}</span>
          {o.text ? (
            <span className="font-sans text-xs text-muted-foreground">{o.text}</span>
          ) : null}
        </li>
      ))}
    </ul>,
    document.body,
  );
}
